import React, { useState, useEffect } from 'react';
import { Droplet, Sun, Zap, ShoppingCart, Smartphone, Award, Home } from 'lucide-react';

// Add this helper function at the top, before the constants
const SAVE_SLOTS = 3;

const PLANT_TYPES = {
  hosta: { name: 'Hosta', cost: 10, sellPrice: 25, waterNeed: 60, lightNeed: 40, tempNeed: 65, growthTime: 8000, unlocked: true, pestResistance: 0.7, preferredSeason: 'spring', climate: 'temperate', rarity: 'common' },
  peacelily: { name: 'Peace Lily', cost: 30, sellPrice: 75, waterNeed: 70, lightNeed: 30, tempNeed: 70, growthTime: 12000, unlocked: false, pestResistance: 0.6, preferredSeason: 'summer', climate: 'tropical', rarity: 'common' },
  cactus: { name: 'Cactus', cost: 20, sellPrice: 50, waterNeed: 20, lightNeed: 90, tempNeed: 75, growthTime: 10000, unlocked: false, pestResistance: 0.9, preferredSeason: 'summer', climate: 'desert', rarity: 'common' },
  elephantear: { name: 'Elephant Ear', cost: 50, sellPrice: 140, waterNeed: 80, lightNeed: 60, tempNeed: 75, growthTime: 15000, unlocked: false, pestResistance: 0.5, preferredSeason: 'summer', climate: 'tropical', rarity: 'common' },
  orchid: { name: 'Orchid', cost: 80, sellPrice: 200, waterNeed: 50, lightNeed: 50, tempNeed: 72, growthTime: 18000, unlocked: false, pestResistance: 0.4, preferredSeason: 'spring', climate: 'temperate', rarity: 'common' },
  succulent: { name: 'Succulent', cost: 25, sellPrice: 60, waterNeed: 15, lightNeed: 85, tempNeed: 78, growthTime: 9000, unlocked: false, pestResistance: 0.95, preferredSeason: 'summer', climate: 'desert', rarity: 'common' },
  fern: { name: 'Fern', cost: 35, sellPrice: 90, waterNeed: 75, lightNeed: 35, tempNeed: 68, growthTime: 11000, unlocked: false, pestResistance: 0.55, preferredSeason: 'fall', climate: 'tropical', rarity: 'common' },
  venustrap: { name: 'Venus Flytrap', cost: 100, sellPrice: 250, waterNeed: 65, lightNeed: 70, tempNeed: 70, growthTime: 20000, unlocked: false, pestResistance: 1.0, feedsOnFlies: true, preferredSeason: 'spring', climate: 'temperate', rarity: 'common' },
  // RARE PLANTS
  blackrose: { name: 'Black Rose', cost: 300, sellPrice: 800, waterNeed: 55, lightNeed: 45, tempNeed: 68, growthTime: 25000, unlocked: false, pestResistance: 0.3, preferredSeason: 'fall', climate: 'temperate', rarity: 'rare', seasonExclusive: 'fall', special: 'Only grows in Fall' },
  rainbowhib: { name: 'Rainbow Hibiscus', cost: 400, sellPrice: 1200, waterNeed: 70, lightNeed: 80, tempNeed: 78, growthTime: 30000, unlocked: false, pestResistance: 0.5, preferredSeason: 'summer', climate: 'tropical', rarity: 'rare', special: 'Changes color each stage' },
  crystalorchid: { name: 'Crystal Orchid', cost: 1000, sellPrice: 3500, waterNeed: 50, lightNeed: 50, tempNeed: 70, growthTime: 45000, unlocked: false, pestResistance: 0.2, preferredSeason: 'spring', climate: 'temperate', rarity: 'ultra', special: 'Glows! +10% value to nearby plants', auraBonus: 0.1 },
  goldenlotus: { name: 'Golden Lotus', cost: 0, sellPrice: 10000, waterNeed: 60, lightNeed: 60, tempNeed: 72, growthTime: 60000, unlocked: false, pestResistance: 1.0, preferredSeason: 'spring', climate: 'temperate', rarity: 'legendary', special: '+25% ALL plant values, Perfect conditions +50% growth', globalAura: 0.25, perfectBonus: 0.5 },
  moonflower: { name: 'Moonflower', cost: 350, sellPrice: 950, waterNeed: 60, lightNeed: 30, tempNeed: 65, growthTime: 28000, unlocked: false, pestResistance: 0.6, preferredSeason: 'fall', climate: 'temperate', rarity: 'rare', special: 'Grows faster at night (winter)' },
  firelily: { name: 'Fire Lily', cost: 450, sellPrice: 1300, waterNeed: 40, lightNeed: 95, tempNeed: 85, growthTime: 32000, unlocked: false, pestResistance: 0.7, preferredSeason: 'summer', climate: 'desert', rarity: 'rare', special: 'Thrives in extreme heat' },
  frostfern: { name: 'Frost Fern', cost: 380, sellPrice: 1100, waterNeed: 80, lightNeed: 40, tempNeed: 50, growthTime: 27000, unlocked: false, pestResistance: 0.65, preferredSeason: 'winter', climate: 'temperate', rarity: 'rare', special: 'Only thrives in winter' },
  thunderbloom: { name: 'Thunder Bloom', cost: 500, sellPrice: 1400, waterNeed: 75, lightNeed: 70, tempNeed: 72, growthTime: 35000, unlocked: false, pestResistance: 0.8, preferredSeason: 'spring', climate: 'temperate', rarity: 'rare', special: 'Immune to season penalties' }
};

const UPGRADES = {
  wateringcan: { name: 'Better Watering Can', cost: 100, purchased: false, effect: 'Water +30 per use', category: 'tools' },
  dripirrigation: { name: 'Drip Irrigation', cost: 500, purchased: false, effect: 'Auto-water every 5s', category: 'automation', perGreenhouse: true },
  humidifier: { name: 'Humidifier', cost: 300, purchased: false, effect: 'Humidity +1/s', category: 'automation', perGreenhouse: true },
  growlight: { name: 'Grow Light', cost: 250, purchased: false, effect: 'Light +1/s', category: 'automation', perGreenhouse: true },
  hvac: { name: 'HVAC System', cost: 400, purchased: false, effect: 'Auto temp control', category: 'automation', perGreenhouse: true },
  smartphone: { name: 'Smart Controls', cost: 800, purchased: false, effect: 'Digital UI sliders', category: 'automation' },
  flyswatter: { name: 'Fly Swatter', cost: 50, purchased: false, effect: 'Remove flies', category: 'pest' },
  pesticide: { name: 'Pesticide Spray', cost: 150, purchased: false, effect: 'Remove aphids', category: 'pest' },
  fertilizer: { name: 'Fertilizer', cost: 200, purchased: false, effect: '+50% growth boost', category: 'tools' },
  climatecontrol: { name: 'Climate Control', cost: 600, purchased: false, effect: '-50% season impact', category: 'automation', perGreenhouse: true },
  advancedhvac: { name: 'Advanced HVAC', cost: 800, purchased: false, effect: 'Negate seasons', category: 'automation', perGreenhouse: true },
  greenhouse2: { name: 'Greenhouse #2', cost: 500, purchased: false, effect: 'Unlock 4 more pots', category: 'greenhouse' },
  greenhouse3: { name: 'Greenhouse #3', cost: 1000, purchased: false, effect: 'Unlock 4 more pots', category: 'greenhouse' },
  peacelilyunlock: { name: 'Peace Lily Seeds', cost: 50, purchased: false, effect: 'Unlock Peace Lily', category: 'seeds' },
  cactusunlock: { name: 'Cactus Seeds', cost: 40, purchased: false, effect: 'Unlock Cactus', category: 'seeds' },
  elephantearunlock: { name: 'Elephant Ear Seeds', cost: 80, purchased: false, effect: 'Unlock Elephant Ear', category: 'seeds' },
  orchidunlock: { name: 'Orchid Seeds', cost: 120, purchased: false, effect: 'Unlock Orchid', category: 'seeds' },
  succulentunlock: { name: 'Succulent Seeds', cost: 45, purchased: false, effect: 'Unlock Succulent', category: 'seeds' },
  fernunlock: { name: 'Fern Spores', cost: 60, purchased: false, effect: 'Unlock Fern', category: 'seeds' },
  venustrapunlock: { name: 'Venus Flytrap Seeds', cost: 150, purchased: false, effect: 'Unlock Venus Flytrap', category: 'seeds' },
  fliespack: { name: 'Pack of Flies (x5)', cost: 30, purchased: false, effect: 'Feed Venus Flytraps', consumable: true, category: 'consumable' },
  mysteryseed: { name: 'Mystery Seed Pack', cost: 500, purchased: false, effect: 'Random rare plant seed!', consumable: true, category: 'consumable' },
  blackroseunlock: { name: 'Black Rose Seeds', cost: 300, purchased: false, effect: 'Unlock Black Rose (Rare)', category: 'rare' },
  rainbowhibunlock: { name: 'Rainbow Hibiscus Seeds', cost: 400, purchased: false, effect: 'Unlock Rainbow Hibiscus (Rare)', category: 'rare' },
  crystalorchidunlock: { name: 'Crystal Orchid Seeds', cost: 1000, purchased: false, effect: 'Unlock Crystal Orchid (Ultra Rare)', category: 'rare' },
  moonflowerunlock: { name: 'Moonflower Seeds', cost: 350, purchased: false, effect: 'Unlock Moonflower (Rare)', category: 'rare' },
  firelilyunlock: { name: 'Fire Lily Seeds', cost: 450, purchased: false, effect: 'Unlock Fire Lily (Rare)', category: 'rare' },
  frostfernunlock: { name: 'Frost Fern Seeds', cost: 380, purchased: false, effect: 'Unlock Frost Fern (Rare)', category: 'rare' },
  thunderbloomunlock: { name: 'Thunder Bloom Seeds', cost: 500, purchased: false, effect: 'Unlock Thunder Bloom (Rare)', category: 'rare' },
  autoharvester: { name: 'Auto-Harvester', cost: 2000, purchased: false, effect: 'Automatically sells Stage 3 plants', category: 'automation' },
  precisionfert: { name: 'Precision Fertilizer', cost: 1800, purchased: false, effect: 'Fertilizer lasts 2x longer (60s)', category: 'automation' }
};

const SEASONS = {
  spring: { name: 'Spring', icon: '🌸', color: '#86efac', globalModifier: 1.1, tempMod: 0, humidityMod: 10, lightMod: 5 },
  summer: { name: 'Summer', icon: '☀️', color: '#fbbf24', globalModifier: 1.0, tempMod: 15, humidityMod: -10, lightMod: 20 },
  fall: { name: 'Fall', icon: '🍂', color: '#fb923c', globalModifier: 1.0, tempMod: -10, humidityMod: 5, lightMod: -5 },
  winter: { name: 'Winter', icon: '❄️', color: '#93c5fd', globalModifier: 0.8, tempMod: -20, humidityMod: 0, lightMod: -15 }
};

const ACHIEVEMENTS = [
  { id: 'first_plant', name: 'Green Thumb', desc: 'Plant your first seed', check: (s) => s.plantsGrown >= 1, icon: '🌱' },
  { id: 'first_harvest', name: 'First Harvest', desc: 'Sell your first plant', check: (s) => s.plantsSold >= 1, icon: '💰' },
  { id: 'money_100', name: 'Entrepreneur', desc: 'Earn $100 total', check: (s) => s.totalEarned >= 100, icon: '💵' },
  { id: 'money_500', name: 'Business Owner', desc: 'Earn $500 total', check: (s) => s.totalEarned >= 500, icon: '💸' },
  { id: 'money_1000', name: 'Tycoon', desc: 'Earn $1000 total', check: (s) => s.totalEarned >= 1000, icon: '🏆' },
  { id: 'all_pots', name: 'Full House', desc: 'Fill all pots in one greenhouse', check: (s) => s.allPotsFilled >= 1, icon: '🪴' },
  { id: 'first_upgrade', name: 'Upgrader', desc: 'Buy your first upgrade', check: (s) => s.upgradesPurchased >= 1, icon: '⬆️' },
  { id: 'automation', name: 'Automated', desc: 'Buy an automation system', check: (s) => s.automationPurchased >= 1, icon: '🤖' },
  { id: 'pest_hunter', name: 'Pest Hunter', desc: 'Kill 10 pests', check: (s) => s.pestsKilled >= 10, icon: '🔫' },
  { id: 'plant_collector', name: 'Collector', desc: 'Unlock 5 plant types', check: (s) => s.plantsUnlocked >= 5, icon: '🌺' },
  { id: 'greenhouse_tycoon', name: 'Greenhouse Tycoon', desc: 'Own all 3 greenhouses', check: (s) => s.greenhousesOwned >= 3, icon: '🏛️' },
  { id: 'full_empire', name: 'Full Empire', desc: 'Fill all 12 pots', check: (s) => s.allPotsEverFilled >= 12, icon: '👑' },
  { id: 'year_streak', name: 'Dedicated Gardener', desc: 'Login for 365 days in a row', check: (s) => s.dailyStreakRecord >= 365, icon: '👑', hidden: true }
];

export default function PlantSimGame() {
  
  const [gameState, setGameState] = useState('menu'); // 'menu', 'playing', 'settings'
  const [currentSaveSlot, setCurrentSaveSlot] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [soundSettings, setSoundSettings] = useState({
    masterVolume: 100,
    sfxVolume: 100,
    waterSound: true,
    cashSound: true,
    growthSound: true,
    notificationSound: true
  });
  const [dailyReward, setDailyReward] = useState({
    lastClaimed: null,
    streakDay: 0,
    canClaim: false
  });
  const [quests, setQuests] = useState({
    daily: [],
    weekly: [],
    permanent: []
  });

  const [money, setMoney] = useState(50);
  const [greenhouses, setGreenhouses] = useState([
    {
      id: 1,
      name: 'Greenhouse #1',
      unlocked: true,
      environment: { temp: 70, humidity: 50, light: 60 },
      pots: [
        { id: 1, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 2, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 3, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 4, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 }
      ],
      upgrades: {
        dripirrigation: false,
        humidifier: false,
        growlight: false,
        hvac: false,
        climatecontrol: false,
        advancedhvac: false
      }
    },
    {
      id: 2,
      name: 'Greenhouse #2',
      unlocked: false,
      environment: { temp: 70, humidity: 50, light: 60 },
      pots: [
        { id: 5, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 6, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 7, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 8, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 }
      ],
      upgrades: {
        dripirrigation: false,
        humidifier: false,
        growlight: false,
        hvac: false,
        climatecontrol: false,
        advancedhvac: false
      }
    },
    {
      id: 3,
      name: 'Greenhouse #3',
      unlocked: false,
      environment: { temp: 70, humidity: 50, light: 60 },
      pots: [
        { id: 9, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 10, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 11, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
        { id: 12, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 }
      ],
      upgrades: {
        dripirrigation: false,
        humidifier: false,
        growlight: false,
        hvac: false,
        climatecontrol: false,
        advancedhvac: false
      }
    }
  ]);
  const [currentSeason, setCurrentSeason] = useState('spring');
  const [seasonTimeLeft, setSeasonTimeLeft] = useState(120);
  const [activeGreenhouse, setActiveGreenhouse] = useState(1);
  const [upgrades, setUpgrades] = useState(UPGRADES);
  const [plantTypes, setPlantTypes] = useState(PLANT_TYPES);
  const [showShop, setShowShop] = useState(false);
  const [showPlantMenu, setShowPlantMenu] = useState(null);
  const [showAchievements, setShowAchievements] = useState(false);
  const [showGreenhouseUpgrades, setShowGreenhouseUpgrades] = useState(null);
  const [flyInventory, setFlyInventory] = useState(0);
  const [stats, setStats] = useState({
    plantsGrown: 0, plantsSold: 0, totalEarned: 0, allPotsFilled: 0,
    upgradesPurchased: 0, automationPurchased: 0, pestsKilled: 0, plantsUnlocked: 1,
    greenhousesOwned: 1, allPotsEverFilled: 0, dailyStreakRecord: 0
  });
  const [unlockedAchievements, setUnlockedAchievements] = useState([]);
  const [notification, setNotification] = useState(null);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);
  const [showDailyRewardPopup, setShowDailyRewardPopup] = useState(false);
  const [pendingDailyReward, setPendingDailyReward] = useState(null);

  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 3000);
  };

  // Save game function
  const saveGame = (slot = currentSaveSlot, showMessage = true) => {
    if (!slot) return;
    
    const saveData = {
      money,
      greenhouses,
      currentSeason,
      seasonTimeLeft,
      upgrades,
      plantTypes,
      flyInventory,
      stats,
      unlockedAchievements,
      dailyReward,
      quests,
      darkMode,
      soundSettings,
      plantStats,
      prestigeLevel,
      questProgress,
      rareCollection,
      storyQuests,
      permanentBonuses,
      
      activeChallenges,
      challengeProgress,
      completedChallenges,
      tutorialCompleted,
      saveSlot: slot,
      savedAt: new Date().toISOString()
    };
    
    localStorage.setItem(`greenhouse_save_slot_${slot}`, JSON.stringify(saveData));
    setLastSaveTime(new Date());
    setShowSaveIndicator(true);
    setTimeout(() => setShowSaveIndicator(false), 2000);
    
    if (showMessage) {
      showNotification('💾 Game Saved!');
    }
  };

  // Auto-save every 30 seconds
  useEffect(() => {
    if (gameState !== 'playing' || !currentSaveSlot) return;
    
    const autoSaveInterval = setInterval(() => {
      saveGame(currentSaveSlot, false);
    }, 30000); // 30 seconds
    
    return () => clearInterval(autoSaveInterval);
  }, [gameState, currentSaveSlot, money, greenhouses, currentSeason, seasonTimeLeft, upgrades, plantTypes, flyInventory, stats, unlockedAchievements, dailyReward, quests]);

  // Daily reward logic
  const DAILY_REWARDS = [
    { day: 1, type: 'money', amount: 25, display: '$25' },
    { day: 2, type: 'flies', amount: 5, display: '5 Flies 🪰' },
    { day: 3, type: 'money', amount: 50, display: '$50' },
    { day: 4, type: 'seed', amount: 1, display: 'Random Seed 🌱' },
    { day: 5, type: 'money', amount: 75, display: '$75' },
    { day: 6, type: 'fertilizer', amount: 1, display: 'Fertilizer Boost ✨' },
    { day: 7, type: 'jackpot', amount: 100, display: '$100 + Seed 🎁' }
  ];

  const checkDailyReward = () => {
    const today = new Date().toDateString();
    const lastClaimed = dailyReward.lastClaimed;
    
    if (lastClaimed === today) {
      console.log('Already claimed today, returning');
      return; // Already claimed today
    }
    
    const lastClaimedDate = lastClaimed ? new Date(lastClaimed) : null;
    const todayDate = new Date();
    let newStreakDay = 1;
    let comebackBonus = false;
    
    if (lastClaimedDate) {
      const daysSince = Math.floor((todayDate - lastClaimedDate) / (1000 * 60 * 60 * 24));
      
      if (daysSince === 1) {
        // Consecutive day
        newStreakDay = (dailyReward.streakDay % 7) + 1;
      } else if (daysSince >= 3) {
        // Comeback bonus
        comebackBonus = true;
        newStreakDay = 1;
      } else {
        // Missed a day, reset
        newStreakDay = 1;
      }
    }
    
    const reward = DAILY_REWARDS[newStreakDay - 1];
    setPendingDailyReward({ ...reward, streakDay: newStreakDay, comebackBonus });
    setShowDailyRewardPopup(true);

    console.log('Setting pending reward:', { ...reward, streakDay: newStreakDay, comebackBonus });
    setPendingDailyReward({ ...reward, streakDay: newStreakDay, comebackBonus });
    setShowDailyRewardPopup(true);
  };

const claimDailyReward = () => {
  if (!pendingDailyReward) return;

  const reward = pendingDailyReward;
  const today = new Date().toDateString();

  // Apply comeback bonus first
  if (reward.comebackBonus) {
    setMoney(m => m + 50);
    setTimeout(() => showNotification('🎉 Welcome back! +$50 bonus!'), 100);
  }

  // Apply daily reward after a short delay if comeback bonus applied
  setTimeout(() => {
    switch (reward.type) {
      case 'money':
        setMoney(m => m + reward.amount);
        showNotification(`💰 Daily Reward: +$${reward.amount}`);
        break;
      case 'flies':
        setFlyInventory(f => f + reward.amount);
        showNotification(`🪰 Daily Reward: +${reward.amount} flies`);
        break;
      case 'seed':
        const unlockedPlants = Object.entries(plantTypes).filter(([_, p]) => p.unlocked);
        if (unlockedPlants.length > 1) {
          const randomPlant = unlockedPlants[Math.floor(Math.random() * unlockedPlants.length)];
          showNotification(`🌱 Daily Reward: ${randomPlant[1].name} seed!`);
        }
        setMoney(m => m + 30);
        break;
      case 'fertilizer':
        setGreenhouses(prev => prev.map(gh => ({
          ...gh,
          pots: gh.pots.map(pot => pot.plant ? { ...pot, fertilized: true, fertilizerTime: 30000 } : pot)
        })));
        showNotification('✨ All plants fertilized for 30s!');
        break;
      case 'jackpot':
        setMoney(m => m + reward.amount);
        const unlockedPlantsJackpot = Object.entries(plantTypes).filter(([_, p]) => p.unlocked);
        if (unlockedPlantsJackpot.length > 1) {
          const randomPlant = unlockedPlantsJackpot[Math.floor(Math.random() * unlockedPlantsJackpot.length)];
          showNotification(`🎁 JACKPOT! +$${reward.amount} + ${randomPlant[1].name} seed!`);
        }
        break;
      default:
        console.warn(`Unknown daily reward type: ${reward?.type}`);
        showNotification('⚠️ Unknown daily reward type received.');
        break;
    }
  }, reward.comebackBonus ? 2000 : 100);

  // Update daily reward state
  setDailyReward({
    lastClaimed: today,
    streakDay: reward.streakDay,
    canClaim: false
  });

  // Update stats for potential 365 achievement
  setStats(prev => ({
    ...prev,
    dailyStreakRecord: Math.max(prev.dailyStreakRecord || 0, reward.streakDay)
  }));

  setShowDailyRewardPopup(false);
  setPendingDailyReward(null);
};

  // Check for daily reward on game start
  useEffect(() => {
    if (gameState === 'playing') {
      setTimeout(() => checkDailyReward(), 1000);
    }
  }, [gameState]);

  // Quest System
  const QUEST_POOL = {
    daily: [
      { id: 'plant_3', name: 'Plant 3 seeds', goal: 3, reward: { type: 'money', amount: 50 }, icon: '🌱', track: 'plantsPlanted' },
      { id: 'water_10', name: 'Water plants 10 times', goal: 10, reward: { type: 'money', amount: 30 }, icon: '💧', track: 'watersUsed' },
      { id: 'harvest_2', name: 'Harvest 2 plants', goal: 2, reward: { type: 'money', amount: 75 }, icon: '💰', track: 'plantsHarvested' },
      { id: 'kill_pests_5', name: 'Kill 5 pests', goal: 5, reward: { type: 'flies', amount: 10 }, icon: '🦟', track: 'pestsKilled' },
      { id: 'earn_100', name: 'Earn $100', goal: 100, reward: { type: 'money', amount: 40 }, icon: '💵', track: 'moneyEarned' }
    ],
    weekly: [
      { id: 'earn_500', name: 'Earn $500 this week', goal: 500, reward: { type: 'money', amount: 150 }, icon: '💵', track: 'moneyEarned' },
      { id: 'grow_orchids', name: 'Grow 5 orchids', goal: 5, reward: { type: 'seed', amount: 1 }, icon: '🌺', track: 'orchidsGrown' },
      { id: 'fertilize_10', name: 'Use fertilizer 10 times', goal: 10, reward: { type: 'money', amount: 100 }, icon: '🌿', track: 'fertilizersUsed' },
      { id: 'full_greenhouse', name: 'Fill all pots in one greenhouse', goal: 1, reward: { type: 'money', amount: 200 }, icon: '🏛️', track: 'fullGreenhouses' }
    ],
    permanent: [
      { id: 'plant_100', name: 'Plant 100 seeds (Lifetime)', goal: 100, reward: { type: 'money', amount: 500 }, icon: '🌱', track: 'plantsGrown' },
      { id: 'earn_5000', name: 'Earn $5000 (Lifetime)', goal: 5000, reward: { type: 'money', amount: 1000 }, icon: '💰', track: 'totalEarned' },
      { id: 'all_plants', name: 'Unlock all plant types', goal: 8, reward: { type: 'money', amount: 300 }, icon: '🏆', track: 'plantsUnlocked' }
    ]
  };



  const [showQuestsModal, setShowQuestsModal] = useState(false);
  const [questProgress, setQuestProgress] = useState({
    plantsPlanted: 0,
    watersUsed: 0,
    plantsHarvested: 0,
    pestsKilledQuest: 0,
    moneyEarned: 0,
    orchidsGrown: 0,
    fertilizersUsed: 0,
    fullGreenhouses: 0
  });
  const [showCodex, setShowCodex] = useState(false);
  const [selectedCodexPlant, setSelectedCodexPlant] = useState(null);
  const [plantStats, setPlantStats] = useState({
    hosta: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    peacelily: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    cactus: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    elephantear: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    orchid: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    succulent: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    fern: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    venustrap: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    blackrose: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    rainbowhib: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    crystalorchid: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    goldenlotus: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    moonflower: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    firelily: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    frostfern: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false },
    thunderbloom: { grown: 0, sold: 0, earned: 0, fastestGrowth: null, discovered: false }
  });
  const [showPhotoMode, setShowPhotoMode] = useState(false);
  const [photoSettings, setPhotoSettings] = useState({
    filter: 'none',
    frame: 'none',
    showSeason: true,
    showNames: true,
    showStats: true,
    caption: ''
  });
  const [prestigeLevel, setPrestigeLevel] = useState(0);
  const [showPrestigeModal, setShowPrestigeModal] = useState(false);
  const [rareCollection, setRareCollection] = useState({
    blackrose: false,
    rainbowhib: false,
    crystalorchid: false,
    goldenlotus: false,
    moonflower: false,
    firelily: false,
    frostfern: false,
    thunderbloom: false
  });
  const [showRareCollection, setShowRareCollection] = useState(false);
  const [storyQuests, setStoryQuests] = useState({
    botanistChallenge: { 
      started: false, 
      currentPart: 0, 
      completed: false,
      partsCompleted: [false, false, false, false, false],
      rewardsClaimed: [false, false, false, false, false]
    },
    masterGardener: { 
      started: false, 
      currentPart: 0, 
      completed: false,
      partsCompleted: [false, false, false, false, false],
      rewardsClaimed: [false, false, false, false, false]
    },
    rareHunter: { 
      started: false, 
      currentPart: 0, 
      completed: false,
      partsCompleted: [false, false, false, false],
      rewardsClaimed: [false, false, false, false]
    }
  });
  const [showStoryQuests, setShowStoryQuests] = useState(false);
  const [permanentBonuses, setPermanentBonuses] = useState({
    growthBonus: 0, // From Botanist Challenge completion
    allPlantsUnlocked: false // From Master Gardener
  });
  
  const [showChallenges, setShowChallenges] = useState(false);
  const [activeChallenges, setActiveChallenges] = useState([]);
  const [challengeProgress, setChallengeProgress] = useState({});
  const [completedChallenges, setCompletedChallenges] = useState([]);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialCompleted, setTutorialCompleted] = useState(false);
  
  
  
  // Removed duplicate state declarations for showChallenges, completedChallenges, tutorialStep and showTutorial
  const [showAlmanac, setShowAlmanac] = useState(false);
  const [almanacTab, setAlmanacTab] = useState('story'); // story, quests, codex, challenges, achievements
  const [showStreakRewards, setShowStreakRewards] = useState(false);

  // Plant lore and tips
  const PLANT_LORE = {
    hosta: {
      description: "A hardy perennial known for its lush foliage and shade tolerance. Perfect for beginners!",
      facts: [
        "Hostas are deer-resistant in real life!",
        "There are over 3,000 varieties of hostas worldwide",
        "Hostas can live for over 20 years with proper care"
      ],
      tips: [
        "Great starter plant",
        "Thrives in winter when others struggle",
        "Low maintenance, high profit"
      ]
    },
    peacelily: {
      description: "An elegant tropical plant with striking white blooms. Known for air purification abilities.",
      facts: [
        "Peace lilies can remove toxins from the air",
        "They're not true lilies - they're in the Araceae family",
        "NASA lists them as one of the top air-cleaning plants"
      ],
      tips: [
        "Keep humidity high for best results",
        "Sensitive to over-watering",
        "Loves warm, stable temperatures"
      ]
    },
    cactus: {
      description: "A desert survivor with minimal water needs. Hardy and low-maintenance.",
      facts: [
        "Cacti can live for over 200 years!",
        "They store water in their thick stems",
        "Some cacti only bloom once a year at night"
      ],
      tips: [
        "Perfect for summer growing",
        "Needs very little water",
        "Highly pest-resistant"
      ]
    },
    elephantear: {
      description: "A dramatic tropical plant with massive, heart-shaped leaves. Makes a bold statement.",
      facts: [
        "Leaves can grow up to 3 feet long in nature",
        "Named for their elephant ear-shaped foliage",
        "Native to tropical Asia and Polynesia"
      ],
      tips: [
        "Needs lots of water and humidity",
        "Grows rapidly in ideal conditions",
        "Watch for pests - they love these leaves"
      ]
    },
    orchid: {
      description: "The queen of flowers. Delicate and beautiful, requiring careful attention to thrive.",
      facts: [
        "Orchids can live for over 100 years!",
        "There are over 25,000 species of orchids",
        "Vanilla flavoring comes from orchid pods"
      ],
      tips: [
        "Requires precise conditions",
        "High value makes it worth the effort",
        "Spring is their preferred season"
      ]
    },
    succulent: {
      description: "A water-storing wonder plant. Trendy and easy to care for.",
      facts: [
        "Succulents can propagate from a single leaf",
        "They can survive months without water",
        "Some succulents change color in different seasons"
      ],
      tips: [
        "Extremely low maintenance",
        "Thrives in desert conditions",
        "Nearly immune to pests"
      ]
    },
    fern: {
      description: "An ancient plant that predates flowering plants. Loves moisture and shade.",
      facts: [
        "Ferns have been around for 360 million years!",
        "They reproduce using spores, not seeds",
        "Some ferns are edible (like fiddleheads)"
      ],
      tips: [
        "Keep humidity consistently high",
        "Prefers shade and cool temperatures",
        "Moderate pest resistance"
      ]
    },
    venustrap: {
      description: "A carnivorous marvel that feeds on flies! Unique and fascinating to grow.",
      facts: [
        "Venus Flytraps only grow naturally in North/South Carolina!",
        "They can count - traps close after 2 touches within 20 seconds",
        "Traps can only close 4-5 times before dying"
      ],
      tips: [
        "Feed flies for faster growth",
        "Completely immune to pests",
        "Requires precise conditions for best results"
      ]
    },
    // ADD RARE PLANTS HERE
  blackrose: {
    description: "A mysterious gothic flower that blooms only under autumn's embrace. Extremely delicate.",
    facts: ["Black roses don't exist naturally - they're very dark red", "Symbolizes rebirth in some cultures", "Requires specific Fall conditions"],
    tips: ["Only grows in Fall season", "Very susceptible to pests", "High value makes it worth it"]
  },
  rainbowhib: {
    description: "A magical hibiscus that shifts through rainbow colors as it grows. A true spectacle.",
    facts: ["Hibiscus flowers make delicious tea", "Rainbow variants are rare mutations", "Each petal can be different"],
    tips: ["Changes color each stage", "Needs high light and heat", "Tropical lover"]
  },
  crystalorchid: {
    description: "An ultra-rare crystalline orchid that emits a soft glow. Enhances nearby plants.",
    facts: ["Crystals refract light naturally", "Boosts surrounding plants", "Takes ages to grow"],
    tips: ["Extremely delicate", "Glowing effect", "+10% value nearby"]
  },
  goldenlotus: {
    description: "The legendary golden lotus, symbol of enlightenment. Grants powerful bonuses.",
    facts: ["Lotus flowers live for millennia", "Golden lotus is mythical", "Divine perfection symbol"],
    tips: ["Cannot be bought", "+25% ALL plants", "Perfect conditions boost"]
  },
  moonflower: {
    description: "A pale, luminous flower that blooms in moonlight. Grows stronger in darkness.",
    facts: ["Blooms at night naturally", "Reflects moonlight", "Night garden favorite"],
    tips: ["Faster in Fall/Winter", "Low light needs", "Moderate pest resistance"]
  },
  firelily: {
    description: "A blazing red lily that thrives in extreme heat. Hardy and valuable.",
    facts: ["Special heat-resistant pigments", "Survives killing temperatures", "Heat-seeking pollinators"],
    tips: ["Loves extreme heat", "Summer specialist", "Very pest resistant"]
  },
  frostfern: {
    description: "An icy blue fern that flourishes in freezing temperatures. Winter specialist.",
    facts: ["Has antifreeze proteins", "Survives below freezing", "Sparkles with ice"],
    tips: ["Winter exclusive", "Loves cold", "Good pest resistance"]
  },
  thunderbloom: {
    description: "A crackling purple flower immune to weather changes. Adaptable and powerful.",
    facts: ["Releases static electricity", "All-weather survivor", "Symbol of resilience"],
    tips: ["Immune to seasons", "Year-round grower", "Very reliable"]
  }
};

  // Story Quest Chains
  const STORY_QUESTS = {
    botanistChallenge: {
      name: "The Botanist's Challenge",
      parts: [
        { goal: 'Grow your first orchid', check: (s, p) => p.orchid?.grown >= 1, reward: { type: 'money', amount: 100 }, desc: 'Plant and grow an orchid to completion' },
        { goal: 'Grow 3 different flower types', check: (s, p) => Object.values(p).filter(plant => plant.grown >= 1).length >= 3, reward: { type: 'rare', amount: 1 }, desc: 'Diversify your garden' },
        { goal: 'Master pest control', check: (s) => s.pestsKilled >= 20, reward: { type: 'money', amount: 300 }, desc: 'Kill 20 pests to prove your skill' },
        { goal: 'Create a tropical paradise', check: (s, p, g) => {
          const tropicalPlants = ['peacelily', 'elephantear', 'fern'];
          return g.some(gh => gh.pots.filter(pot => pot.plant && tropicalPlants.includes(pot.plant)).length >= 4);
        }, reward: { type: 'title', amount: 1 }, desc: 'Fill a greenhouse with tropical plants' },
        { goal: 'Achieve perfect health', check: (s) => s.perfectHealthCount >= 1, reward: { type: 'permanent', amount: 0.1 }, desc: 'Get all plants to 90%+ health simultaneously' }
      ]
    },
    masterGardener: {
      name: "Master Gardener Path",
      parts: [
        { goal: 'Unlock all 8 basic plants', check: (s) => s.plantsUnlocked >= 8, reward: { type: 'money', amount: 200 }, desc: 'Complete your basic plant collection' },
        { goal: 'Master any plant', check: (s, p) => Object.values(p).some(plant => plant.grown >= 50), reward: { type: 'money', amount: 500 }, desc: 'Grow a single plant 50 times' },
        { goal: 'Earn $5000 total', check: (s) => s.totalEarned >= 5000, reward: { type: 'rare', amount: 1 }, desc: 'Build your fortune' },
        { goal: 'Reach 30-day streak', check: (s) => s.dailyStreakRecord >= 30, reward: { type: 'rare', amount: 1 }, desc: 'Show dedication over time' },
        { goal: 'Master all basic plants', check: (s, p) => {
          const basicPlants = ['hosta', 'peacelily', 'cactus', 'elephantear', 'orchid', 'succulent', 'fern', 'venustrap'];
          return basicPlants.every(plant => p[plant]?.grown >= 50);
        }, reward: { type: 'legendary', amount: 1 }, desc: 'Achieve true mastery' }
      ]
    },
    rareHunter: {
      name: "Rare Plant Hunter",
      parts: [
        { goal: 'Discover your first rare plant', check: (s, p) => {
          const rarePlants = ['blackrose', 'rainbowhib', 'crystalorchid', 'moonflower', 'firelily', 'frostfern', 'thunderbloom'];
          return rarePlants.some(plant => p[plant]?.discovered);
        }, reward: { type: 'money', amount: 250 }, desc: 'Unlock and grow any rare plant' },
        { goal: 'Collect 3 rare plants', check: (s, p) => {
          const rarePlants = ['blackrose', 'rainbowhib', 'crystalorchid', 'moonflower', 'firelily', 'frostfern', 'thunderbloom'];
          return rarePlants.filter(plant => p[plant]?.discovered).length >= 3;
        }, reward: { type: 'rare', amount: 1 }, desc: 'Expand your rare collection' },
        { goal: 'Grow a Crystal Orchid', check: (s, p) => p.crystalorchid?.grown >= 1, reward: { type: 'money', amount: 1000 }, desc: 'Successfully grow an ultra-rare plant' },
        { goal: 'Collect all 7 rare plants', check: (s, p) => {
          const rarePlants = ['blackrose', 'rainbowhib', 'crystalorchid', 'moonflower', 'firelily', 'frostfern', 'thunderbloom'];
          return rarePlants.filter(plant => p[plant]?.discovered).length >= 7;
        }, reward: { type: 'legendary', amount: 1 }, desc: 'Complete the rare collection' }
      ]
    }
  };

  // Rare plant lore
  const RARE_PLANT_LORE = {
    blackrose: {
      description: "A mysterious gothic flower that blooms only under autumn's embrace. Extremely delicate.",
      facts: ["Black roses don't exist naturally - they're very dark red", "Symbolizes rebirth in some cultures", "Requires specific Fall conditions"],
      tips: ["Only grows in Fall season", "Very susceptible to pests", "High value makes it worth it"]
    },
    rainbowhib: {
      description: "A magical hibiscus that shifts through rainbow colors as it grows. A true spectacle.",
      facts: ["Hibiscus flowers make delicious tea", "Rainbow variants are rare mutations", "Each petal can be different"],
      tips: ["Changes color each stage", "Needs high light and heat", "Tropical lover"]
    },
    crystalorchid: {
      description: "An ultra-rare crystalline orchid that emits a soft glow. Enhances nearby plants.",
      facts: ["Crystals refract light naturally", "Boosts surrounding plants", "Takes ages to grow"],
      tips: ["Extremely delicate", "Glowing effect", "+10% value nearby"]
    },
    goldenlotus: {
      description: "The legendary golden lotus, symbol of enlightenment. Grants powerful bonuses.",
      facts: ["Lotus flowers live for millennia", "Golden lotus is mythical", "Divine perfection symbol"],
      tips: ["Cannot be bought", "+25% ALL plants", "Perfect conditions boost"]
    },
    moonflower: {
      description: "A pale, luminous flower that blooms in moonlight. Grows stronger in darkness.",
      facts: ["Blooms at night naturally", "Reflects moonlight", "Night garden favorite"],
      tips: ["Faster in Fall/Winter", "Low light needs", "Moderate pest resistance"]
    },
    firelily: {
      description: "A blazing red lily that thrives in extreme heat. Hardy and valuable.",
      facts: ["Special heat-resistant pigments", "Survives killing temperatures", "Heat-seeking pollinators"],
      tips: ["Loves extreme heat", "Summer specialist", "Very pest resistant"]
    },
    frostfern: {
      description: "An icy blue fern that flourishes in freezing temperatures. Winter specialist.",
      facts: ["Has antifreeze proteins", "Survives below freezing", "Sparkles with ice"],
      tips: ["Winter exclusive", "Loves cold", "Good pest resistance"]
    },
    thunderbloom: {
      description: "A crackling purple flower immune to weather changes. Adaptable and powerful.",
      facts: ["Releases static electricity", "All-weather survivor", "Symbol of resilience"],
      tips: ["Immune to seasons", "Year-round grower", "Very reliable"]
    }
  };

  

  // Weekly Challenges
  const CHALLENGE_TYPES = [
    {
      id: 'speedrun',
      name: 'Speedrun',
      desc: 'Earn $1000 in 10 minutes',
      icon: '⚡',
      goal: 1000,
      timeLimit: 600, // seconds
      reward: { type: 'money', amount: 500, display: '$500' }
    },
    {
      id: 'minimalist',
      name: 'Minimalist',
      desc: 'Earn $500 using only 2 pots',
      icon: '🎯',
      goal: 500,
      maxPots: 2,
      reward: { type: 'rare', amount: 1, display: 'Rare Seed' }
    },
    {
      id: 'perfectionist',
      name: 'Perfectionist',
      desc: 'Keep all plants at 100% health for 5 minutes',
      icon: '💯',
      goal: 300, // seconds
      reward: { type: 'money', amount: 300, display: '$300' }
    },
    {
      id: 'pestmaster',
      name: 'Pest Master',
      desc: 'Clear 20 pests in 1 minute',
      icon: '🔫',
      goal: 20,
      timeLimit: 60, // seconds
      reward: { type: 'flies', amount: 30, display: '30 Flies' }
    }
  ];

  const generateWeeklyChallenges = () => {
    const shuffled = [...CHALLENGE_TYPES].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3).map(challenge => ({
      ...challenge,
      active: false,
      completed: false,
      claimed: false,
      progress: 0,
      startTime: null
    }));
  };

  const getMasteryLevel = (plantKey) => {
    const grown = plantStats[plantKey]?.grown || 0;
    if (grown >= 50) return { level: 'MASTERED', bonus: 0.10, color: '#fbbf24' };
    if (grown >= 25) return { level: 'Expert', bonus: 0.05, color: '#8b5cf6' };
    if (grown >= 10) return { level: 'Experienced', bonus: 0.02, color: '#3b82f6' };
    if (grown >= 1) return { level: 'Discovered', bonus: 0, color: '#64748b' };
    return { level: 'Locked', bonus: 0, color: '#334155' };
  };

  const getMasteredCount = () => {
    return Object.keys(plantStats).filter(key => plantStats[key].grown >= 50).length;
  };

  const getDiscoveredCount = () => {
    return Object.keys(plantStats).filter(key => plantStats[key].discovered).length;
  };

  const generateDailyQuests = () => {
    const shuffled = [...QUEST_POOL.daily].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3).map(q => ({ ...q, progress: 0, claimed: false }));
  };

  const generateWeeklyQuests = () => {
    const shuffled = [...QUEST_POOL.weekly].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 2).map(q => ({ ...q, progress: 0, claimed: false }));
  };

  // Initialize quests if empty
  useEffect(() => {
    if (gameState === 'playing' && quests.daily.length === 0) {
      setQuests({
        daily: generateDailyQuests(),
        weekly: generateWeeklyQuests(),
        permanent: QUEST_POOL.permanent.map(q => ({ ...q, progress: 0, claimed: false }))
      });
    }
  }, [gameState]);

  // Check and reset daily/weekly quests
  useEffect(() => {
    if (gameState !== 'playing') return;
    
    const checkQuestReset = setInterval(() => {
      const now = new Date();
      const lastCheck = localStorage.getItem('last_quest_check');
      
      if (lastCheck) {
        const lastDate = new Date(lastCheck);
        
        // Check daily reset (midnight)
        if (lastDate.getDate() !== now.getDate()) {
          setQuests(prev => ({ ...prev, daily: generateDailyQuests() }));
          setQuestProgress({
            plantsPlanted: 0,
            watersUsed: 0,
            plantsHarvested: 0,
            pestsKilledQuest: 0,
            moneyEarned: 0,
            orchidsGrown: 0,
            fertilizersUsed: 0,
            fullGreenhouses: 0
          });
        }
        
        // Check weekly reset (Monday)
        if (lastDate.getDay() !== 1 && now.getDay() === 1) {
          setQuests(prev => ({ ...prev, weekly: generateWeeklyQuests() }));
        }
      }
      
      localStorage.setItem('last_quest_check', now.toISOString());
    }, 60000); // Check every minute
    
    return () => clearInterval(checkQuestReset);
  }, [gameState]);

  // Update quest progress
  useEffect(() => {
    setQuests(prev => ({
      daily: prev.daily.map(q => ({
        ...q,
        progress: q.track === 'plantsGrown' ? stats.plantsGrown :
                 q.track === 'totalEarned' ? stats.totalEarned :
                 q.track === 'plantsUnlocked' ? stats.plantsUnlocked :
                 q.track === 'pestsKilled' ? stats.pestsKilled :
                 questProgress[q.track] || 0
      })),
      weekly: prev.weekly.map(q => ({
        ...q,
        progress: q.track === 'plantsGrown' ? stats.plantsGrown :
                 q.track === 'totalEarned' ? stats.totalEarned :
                 questProgress[q.track] || 0
      })),
      permanent: prev.permanent.map(q => ({
        ...q,
        progress: q.track === 'plantsGrown' ? stats.plantsGrown :
                 q.track === 'totalEarned' ? stats.totalEarned :
                 q.track === 'plantsUnlocked' ? stats.plantsUnlocked :
                 0
      }))
    }));
  }, [stats, questProgress]);

  const claimQuestReward = (questType, questId) => {
    const quest = quests[questType].find(q => q.id === questId);
    if (!quest || quest.claimed || quest.progress < quest.goal) return;
    
    // Apply reward
    if (quest.reward.type === 'money') {
      setMoney(m => m + quest.reward.amount);
      showNotification(`💰 Quest Complete! +$${quest.reward.amount}`);
    } else if (quest.reward.type === 'flies') {
      setFlyInventory(f => f + quest.reward.amount);
      showNotification(`🪰 Quest Complete! +${quest.reward.amount} flies`);
    } else if (quest.reward.type === 'seed') {
      showNotification(`🌱 Quest Complete! +Random Seed`);
      setMoney(m => m + 50);
    }
    
    // Mark as claimed
    setQuests(prev => ({
      ...prev,
      [questType]: prev[questType].map(q => 
        q.id === questId ? { ...q, claimed: true } : q
      )
    }));
  };

  const canPrestige = () => {
    return stats.totalEarned >= 10000 && 
           stats.greenhousesOwned >= 3 && 
           unlockedAchievements.length >= 5;
  };

  // Challenge functions
  const startChallenge = (challengeId) => {
    setActiveChallenges(prev => prev.map(c => 
      c.id === challengeId ? { ...c, active: true, startTime: Date.now(), progress: 0 } : c
    ));
    setChallengeProgress(prev => ({ ...prev, [challengeId]: 0 }));
    const challenge = CHALLENGE_TYPES.find(t => t.id === challengeId);
    showNotification(`🏆 Challenge Started: ${challenge?.name || 'Challenge'}`);
  };

  const claimChallengeReward = (challengeId) => {
    const challenge = activeChallenges.find(c => c.id === challengeId);
    if (!challenge || !challenge.completed || challenge.claimed) return;

    const reward = challenge.reward;
    if (reward.type === 'money') {
      setMoney(m => m + reward.amount);
      showNotification(`💰 Challenge Reward: +$${reward.amount}`);
    } else if (reward.type === 'rare') {
      const rareSeeds = ['blackrose', 'rainbowhib', 'moonflower', 'firelily', 'frostfern', 'thunderbloom'];
      const randomRare = rareSeeds[Math.floor(Math.random() * rareSeeds.length)];
      setPlantTypes(prev => ({ ...prev, [randomRare]: { ...prev[randomRare], unlocked: true } }));
      setRareCollection(prev => ({ ...prev, [randomRare]: true }));
      showNotification(`✨ Challenge Reward: ${plantTypes[randomRare].name} unlocked!`);
    } else if (reward.type === 'flies') {
      setFlyInventory(f => f + reward.amount);
      showNotification(`🪰 Challenge Reward: +${reward.amount} flies`);
    }

    setActiveChallenges(prev => prev.map(c =>
      c.id === challengeId ? { ...c, claimed: true } : c
    ));
    setCompletedChallenges(prev => [...prev, challengeId]);
  };

  const doPrestige = () => {
    if (!canPrestige()) {
      showNotification('❌ Prestige requirements not met!');
      return;
    }

    const newPrestigeLevel = prestigeLevel + 1;
    const startMoney = newPrestigeLevel >= 3 ? 500 : 50;

    // Reset game state but keep prestige benefits
    setMoney(startMoney);
    setFlyInventory(0);
    setUpgrades(UPGRADES);
    setPlantTypes({
      ...PLANT_TYPES,
      hosta: { ...PLANT_TYPES.hosta, unlocked: true },
      peacelily: { ...PLANT_TYPES.peacelily, unlocked: newPrestigeLevel >= 5 },
      cactus: { ...PLANT_TYPES.cactus, unlocked: newPrestigeLevel >= 5 },
      elephantear: { ...PLANT_TYPES.elephantear, unlocked: newPrestigeLevel >= 5 },
      orchid: { ...PLANT_TYPES.orchid, unlocked: newPrestigeLevel >= 5 },
      succulent: { ...PLANT_TYPES.succulent, unlocked: newPrestigeLevel >= 5 },
      fern: { ...PLANT_TYPES.fern, unlocked: newPrestigeLevel >= 5 },
      venustrap: { ...PLANT_TYPES.venustrap, unlocked: newPrestigeLevel >= 5 }
    });

    // Reset greenhouses
    const greenhouse1Unlocked = true;
    const greenhouse2Unlocked = newPrestigeLevel >= 4;
    const greenhouse3Unlocked = false;

    setGreenhouses([
      {
        id: 1,
        name: 'Greenhouse #1',
        unlocked: greenhouse1Unlocked,
        environment: { temp: 70, humidity: 50, light: 60 },
        pots: [
          { id: 1, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 2, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 3, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 4, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 }
        ],
        upgrades: {
          dripirrigation: false,
          humidifier: false,
          growlight: false,
          hvac: false,
          climatecontrol: false,
          advancedhvac: false
        }
      },
      {
        id: 2,
        name: 'Greenhouse #2',
        unlocked: greenhouse2Unlocked,
        environment: { temp: 70, humidity: 50, light: 60 },
        pots: [
          { id: 5, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 6, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 7, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 8, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 }
        ],
        upgrades: {
          dripirrigation: false,
          humidifier: false,
          growlight: false,
          hvac: false,
          climatecontrol: false,
          advancedhvac: false
        }
      },
      {
        id: 3,
        name: 'Greenhouse #3',
        unlocked: greenhouse3Unlocked,
        environment: { temp: 70, humidity: 50, light: 60 },
        pots: [
          { id: 9, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 10, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 11, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 },
          { id: 12, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0 }
        ],
        upgrades: {
          dripirrigation: false,
          humidifier: false,
          growlight: false,
          hvac: false,
          climatecontrol: false,
          advancedhvac: false
        }
      }
    ]);

    // Reset stats but keep some achievements
    setStats({
      plantsGrown: 0,
      plantsSold: 0,
      totalEarned: 0,
      allPotsFilled: 0,
      upgradesPurchased: 0,
      automationPurchased: 0,
      pestsKilled: 0,
      plantsUnlocked: newPrestigeLevel >= 5 ? 8 : 1,
      greenhousesOwned: greenhouse2Unlocked ? 2 : 1,
      allPotsEverFilled: 0,
      dailyStreakRecord: stats.dailyStreakRecord // KEEP STREAK
    });

    setPrestigeLevel(newPrestigeLevel);
    setShowPrestigeModal(false);
    showNotification(`🔄 Prestige ${newPrestigeLevel} Complete! +${25 * newPrestigeLevel}% sell bonus!`);
  };

  // Check and update story quest progress
  useEffect(() => {
    if (gameState !== 'playing') return;

    

    // Initialize weekly challenges
    if (activeChallenges.length === 0 && prestigeLevel >= 1) {
      setActiveChallenges(generateWeeklyChallenges());
    }

    // Check for tutorial on new game
    if (!tutorialCompleted && stats.plantsGrown === 0 && gameState === 'playing') {
      setShowTutorial(true);
    }

    // Check each quest chain
    Object.keys(STORY_QUESTS).forEach(questKey => {
      const quest = storyQuests[questKey];
      const questData = STORY_QUESTS[questKey];
      
      if (quest.completed) return; // Skip completed quests

      // Auto-start first quest if not started
      if (!quest.started && questKey === 'botanistChallenge') {
        setStoryQuests(prev => ({
          ...prev,
          [questKey]: { ...prev[questKey], started: true }
        }));
      }

      // Check each part
      questData.parts.forEach((part, index) => {
        if (quest.partsCompleted[index] || quest.rewardsClaimed[index]) return;

        // Check if this part is complete
        let isComplete = false;
        try {
          isComplete = part.check(stats, plantStats, greenhouses);
        } catch (e) {
          isComplete = false;
        }

        if (isComplete && !quest.partsCompleted[index]) {
          // Mark part as complete
          setStoryQuests(prev => {
            const updated = { ...prev };
            updated[questKey].partsCompleted[index] = true;
            updated[questKey].currentPart = Math.max(updated[questKey].currentPart, index);
            return updated;
          });
          
          showNotification(`📖 Quest Progress: "${part.goal}" completed!`);
        }
      });

      // Check if entire quest chain is complete
      const allPartsComplete = quest.partsCompleted.every(p => p);
      if (allPartsComplete && !quest.completed) {
        setStoryQuests(prev => ({
          ...prev,
          [questKey]: { ...prev[questKey], completed: true }
        }));
        showNotification(`🎉 QUEST CHAIN COMPLETE: ${questData.name}!`);
      }
    });
  }, [stats, plantStats, greenhouses, gameState]);

  // Claim story quest reward
  const claimStoryReward = (questKey, partIndex) => {
    const questData = STORY_QUESTS[questKey];
    const part = questData.parts[partIndex];
    
    if (!storyQuests[questKey].partsCompleted[partIndex] || storyQuests[questKey].rewardsClaimed[partIndex]) {
      return;
    }

    // Apply reward
    switch (part.reward.type) {
      case 'money':
        setMoney(m => m + part.reward.amount);
        showNotification(`💰 Quest Reward: +$${part.reward.amount}`);
        break;
      case 'rare':
        // Give random rare seed
        const rareSeeds = ['blackrose', 'rainbowhib', 'moonflower', 'firelily', 'frostfern', 'thunderbloom'];
        const randomRare = rareSeeds[Math.floor(Math.random() * rareSeeds.length)];
        setPlantTypes(prev => ({ ...prev, [randomRare]: { ...prev[randomRare], unlocked: true } }));
        setRareCollection(prev => ({ ...prev, [randomRare]: true }));
        showNotification(`✨ Quest Reward: ${plantTypes[randomRare].name} unlocked!`);
        break;
      case 'title':
        showNotification(`🏆 Quest Reward: "Tropics Expert" title earned!`);
        break;
      case 'permanent':
        setPermanentBonuses(prev => ({
          ...prev,
          growthBonus: prev.growthBonus + part.reward.amount
        }));
        showNotification(`⚡ Quest Reward: +${Math.round(part.reward.amount * 100)}% permanent growth bonus!`);
        break;
      case 'legendary':
        // Give Golden Lotus
        setPlantTypes(prev => ({ ...prev, goldenlotus: { ...prev.goldenlotus, unlocked: true } }));
        setRareCollection(prev => ({ ...prev, goldenlotus: true }));
        showNotification(`👑 LEGENDARY REWARD: Golden Lotus unlocked!`);
        break;
        default:
        // Fallback for unknown reward types
        console.warn(`Unknown quest reward type: ${part.reward?.type}`, part.reward);
        showNotification(`⚠️ Quest Reward: unknown reward type "${part.reward?.type}"`);
        break;
    }

    // Mark reward as claimed
    setStoryQuests(prev => {
      const updated = { ...prev };
      updated[questKey].rewardsClaimed[partIndex] = true;
      return updated;
    });

    // Check if we should start next quest chain
    if (questKey === 'botanistChallenge' && storyQuests.botanistChallenge.completed && !storyQuests.masterGardener.started) {
      setStoryQuests(prev => ({
        ...prev,
        masterGardener: { ...prev.masterGardener, started: true }
      }));
      showNotification(`📖 New Quest Chain: ${STORY_QUESTS.masterGardener.name}!`);
    }
    if (questKey === 'masterGardener' && storyQuests.masterGardener.completed && !storyQuests.rareHunter.started) {
      setStoryQuests(prev => ({
        ...prev,
        rareHunter: { ...prev.rareHunter, started: true }
      }));
      showNotification(`📖 New Quest Chain: ${STORY_QUESTS.rareHunter.name}!`);
    }
  };

  const getSeasonModifier = (plant, greenhouse) => {
    const season = SEASONS[currentSeason];
    const plantData = plantTypes[plant];
    
    if (greenhouse.upgrades.advancedhvac) return 1.0;
    if (greenhouse.upgrades.climatecontrol) {
      const mod = season.globalModifier;
      return 1.0 + ((mod - 1.0) * 0.5);
    }

    let modifier = season.globalModifier;
    
    if (plantData.climate === 'desert' && currentSeason === 'summer') modifier *= 1.2;
    if (plantData.climate === 'tropical' && currentSeason === 'winter') modifier *= 0.7;
    if (plantData.climate === 'tropical' && currentSeason === 'summer') modifier *= 1.15;
    if (plantData.preferredSeason === currentSeason) modifier *= 1.1;

    return modifier;
  };

  useEffect(() => {
    ACHIEVEMENTS.forEach(achievement => {
      if (!unlockedAchievements.includes(achievement.id) && achievement.check(stats)) {
        setUnlockedAchievements(prev => [...prev, achievement.id]);
        showNotification(`🏆 Achievement Unlocked: ${achievement.name}!`);
      }
    });
  }, [stats, unlockedAchievements]);

  useEffect(() => {
    if (gameState !== 'playing') return;
    const seasonTimer = setInterval(() => {
      setSeasonTimeLeft(prev => {
        if (prev <= 1) {
          const seasons = ['spring', 'summer', 'fall', 'winter'];
          const currentIndex = seasons.indexOf(currentSeason);
          const nextSeason = seasons[(currentIndex + 1) % 4];
          setCurrentSeason(nextSeason);
          showNotification(`${SEASONS[nextSeason].icon} ${SEASONS[nextSeason].name} has arrived!`);
          return 120;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(seasonTimer);
  }, [gameState,currentSeason]);

  useEffect(() => {
    if (gameState !== 'playing') return;   
    const interval = setInterval(() => {
      setGreenhouses(prevGreenhouses => prevGreenhouses.map(greenhouse => {
        if (!greenhouse.unlocked) return greenhouse;

        const season = SEASONS[currentSeason];
        let newEnv = { ...greenhouse.environment };
        
        newEnv.temp = Math.max(50, newEnv.temp - 0.5);
        newEnv.humidity = Math.max(0, newEnv.humidity - 0.5);
        newEnv.light = Math.max(0, newEnv.light - 0.5);

        if (!greenhouse.upgrades.advancedhvac) {
          newEnv.temp += season.tempMod * 0.1;
          newEnv.humidity += season.humidityMod * 0.1;
          newEnv.light += season.lightMod * 0.1;
        }

        if (greenhouse.upgrades.humidifier) newEnv.humidity = Math.min(100, newEnv.humidity + 1);
        if (greenhouse.upgrades.growlight) newEnv.light = Math.min(100, newEnv.light + 1);
        if (greenhouse.upgrades.hvac) newEnv.temp = 70;

        const newPots = greenhouse.pots.map(pot => {
          if (!pot.plant) return pot;
          const pt = plantTypes[pot.plant];
          let w = pot.water - 1;
          if (greenhouse.upgrades.dripirrigation) w = Math.min(100, w + 4);
          
          let ft = pot.fertilizerTime;
          if (pot.fertilized && ft > 0) {
            ft -= 1000;
            if (ft <= 0) return { ...pot, fertilized: false, fertilizerTime: 0, water: Math.max(0, w) };
          }
    
          const wh = Math.max(0, 100 - Math.abs(w - pt.waterNeed));
          const lh = Math.max(0, 100 - Math.abs(newEnv.light - pt.lightNeed));
          const th = Math.max(0, 100 - Math.abs(newEnv.temp - pt.tempNeed));
          let oh = (wh + lh + th) / 3;
          if (pot.pest) oh *= 0.5;
    
          let g = pot.growth;
          // Plants need ALL conditions above minimum threshold to grow
          if (oh > 50 && wh > 20 && lh > 20 && th > 20) {
            let gr = (oh / 100) * 100;
            if (pot.fertilized) gr *= 1.5;
  
            const seasonMod = getSeasonModifier(pot.plant, greenhouse);
            gr *= seasonMod;
  
            g += gr;
          }
    
          let st = pot.stage;
          if (g >= pt.growthTime && st < 3) { 
            st++; 
            g = 0;

            // Tutorial progress - advance when plant reaches stage 2 (50% grown)
            if (showTutorial && tutorialStep === 2 && st === 2) {
              setTimeout(() => setTutorialStep(3), 500);
            }
  
            // Tutorial progress - advance when plant reaches stage 3 (fully grown/4-4)
            if (showTutorial && tutorialStep === 3 && st === 3) {
              setTimeout(() => setTutorialStep(4), 500);
            }
          }
          
          // Auto-harvester: automatically harvest Stage 3 plants
          if (st === 3 && upgrades.autoharvester.purchased) {
            setTimeout(() => harvestPlant(pot.id), 100);
          }
    
          let pest = pot.pest;
                   // Seasonal/event modifiers removed — use base spawn chance
          if (!pest && Math.random() < (1 - pt.pestResistance) * 0.002) {
            pest = Math.random() < 0.5 ? 'fly' : 'aphid';
          }
    
            return { ...pot, water: Math.max(0, w), growth: g, stage: st, pest, fertilizerTime: ft };
          });

        return { ...greenhouse, environment: newEnv, pots: newPots };
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, [plantTypes, currentSeason, upgrades.autoharvester, gameState]);

  const waterPlant = (potId) => {
    setGreenhouses(prev => prev.map(gh => ({
      ...gh,
      pots: gh.pots.map(pot => 
        pot.id === potId ? { ...pot, water: Math.min(100, pot.water + (upgrades.wateringcan.purchased ? 30 : 15)) } : pot
      )
    })));
    setQuestProgress(prev => ({ ...prev, watersUsed: prev.watersUsed + 1 }));

    // Tutorial progress
    if (showTutorial && tutorialStep === 1) {
      setTimeout(() => setTutorialStep(2), 500);
    }
  };

  const fertilizePlant = (potId) => {
    if (!upgrades.fertilizer.purchased) return;
    const duration = upgrades.precisionfert.purchased ? 60000 : 30000;
    const seconds = duration / 1000;
    setGreenhouses(prev => prev.map(gh => ({
      ...gh,
      pots: gh.pots.map(pot => 
        pot.id === potId ? { ...pot, fertilized: true, fertilizerTime: duration } : pot
      )
    })));
    setQuestProgress(prev => ({ ...prev, fertilizersUsed: prev.fertilizersUsed + 1 }));
    showNotification(`🌿 Fertilizer applied! +50% growth for ${seconds}s`);


  };

  const removePest = (potId, type) => {
    let pot = null;
    greenhouses.forEach(gh => {
      const foundPot = gh.pots.find(p => p.id === potId);
      if (foundPot) pot = foundPot;
    });
    
    if (!pot?.pest) return;
    if (type === 'fly' && !upgrades.flyswatter.purchased) { showNotification('❌ Buy Fly Swatter first!'); return; }
    if (type === 'aphid' && !upgrades.pesticide.purchased) { showNotification('❌ Buy Pesticide first!'); return; }
    
    setGreenhouses(prev => prev.map(gh => ({
      ...gh,
      pots: gh.pots.map(p => p.id === potId ? { ...p, pest: null } : p)
    })));
    
    if (type === 'fly') { setFlyInventory(f => f + 1); showNotification('🪰 Fly caught!'); }
    else showNotification('🦟 Aphids eliminated!');
    setStats(p => ({ ...p, pestsKilled: p.pestsKilled + 1 }));
    setQuestProgress(prev => ({ ...prev, pestsKilledQuest: prev.pestsKilledQuest + 1 }));
    
    

    // Track for Pest Master challenge
    const pestChallenge = activeChallenges.find(c => c.id === 'pestmaster' && c.active);
    if (pestChallenge) {
      setChallengeProgress(prev => ({
        ...prev,
        pestmaster: (prev.pestmaster || 0) + 1
      }));
    }
  };

  const feedVenusTrap = (potId) => {
    let pot = null;
    greenhouses.forEach(gh => {
      const foundPot = gh.pots.find(p => p.id === potId);
      if (foundPot) pot = foundPot;
    });
    
    if (!pot?.plant || !plantTypes[pot.plant].feedsOnFlies) return;
    if (flyInventory < 1) { showNotification('❌ No flies!'); return; }
    
    setFlyInventory(f => f - 1);
    setGreenhouses(prev => prev.map(gh => ({
      ...gh,
      pots: gh.pots.map(p => 
        p.id === potId ? { ...p, growth: Math.min(plantTypes[p.plant].growthTime, p.growth + 2000) } : p
      )
    })));
    showNotification('🪰 Venus Flytrap fed!');
  };

  const plantSeed = (potId, type) => {
    if (money >= plantTypes[type].cost) {
      setMoney(m => m - plantTypes[type].cost);
      setGreenhouses(prev => prev.map(gh => ({
        ...gh,
        pots: gh.pots.map(pot => 
          pot.id === potId ? { ...pot, plant: type, water: 50, growth: 0, stage: 0, pest: null, growthStartTime: Date.now() } : pot
        )
      })));
      setShowPlantMenu(null);
      
      // Track plant stats
      setPlantStats(prev => ({
        ...prev,
        [type]: {
          ...prev[type],
          grown: prev[type].grown + 1,
          discovered: true
        }
      }));
      
      const totalPlanted = greenhouses.reduce((acc, gh) => acc + gh.pots.filter(p => p.plant).length, 0) + 1;
      setStats(p => ({ 
        ...p, 
        plantsGrown: p.plantsGrown + 1,
        allPotsFilled: greenhouses.find(gh => gh.id === activeGreenhouse)?.pots.filter(pt => pt.plant).length + 1 >= 4 ? p.allPotsFilled + 1 : p.allPotsFilled,
        allPotsEverFilled: Math.max(p.allPotsEverFilled, totalPlanted)
      }));
      setQuestProgress(prev => ({ 
        ...prev, 
        plantsPlanted: prev.plantsPlanted + 1,
        orchidsGrown: type === 'orchid' ? prev.orchidsGrown + 1 : prev.orchidsGrown
      }));

      // Tutorial progress
      if (showTutorial && tutorialStep === 0) {
        setTimeout(() => setTutorialStep(1), 500);
      }
    }
  };

  const harvestPlant = (potId) => {
    let pot = null;
    let greenhouseWithPot = null;
    greenhouses.forEach(gh => {
      const foundPot = gh.pots.find(p => p.id === potId);
      if (foundPot) {
        pot = foundPot;
        greenhouseWithPot = gh;
      }
    });
    
    if (pot?.plant && pot.stage === 3) {
      const plantType = pot.plant;
      const plantData = plantTypes[plantType];
      const mastery = getMasteryLevel(plantType);
      
      // Calculate bonuses
      let basePrice = plantData.sellPrice;
      
      // Apply aura bonuses from special plants in same greenhouse
      let auraBonus = 0;
      greenhouseWithPot?.pots.forEach(p => {
        if (p.plant && p.stage === 3) {
          const pData = plantTypes[p.plant];
          if (pData.auraBonus) auraBonus += pData.auraBonus; // Crystal Orchid
        }
      });
      
      // Check for Golden Lotus global aura
      let globalAura = 0;
      greenhouses.forEach(gh => {
        gh.pots.forEach(p => {
          if (p.plant === 'goldenlotus' && p.stage === 3) {
            globalAura = plantTypes.goldenlotus.globalAura;
          }
        });
      });

      
      
      const totalBonus = 1 + mastery.bonus + (prestigeLevel * 0.25) + auraBonus + globalAura;
      const earn = Math.floor(basePrice * totalBonus);
      
      // Track growth time
      const growthTime = pot.growthStartTime ? Date.now() - pot.growthStartTime : null;
      
      // 2% chance for rare seed drop (only from common plants)
      let rareSeedDropped = false;
      if (plantData.rarity === 'common' && Math.random() < 0.02) {
        rareSeedDropped = true;
        const rareSeeds = ['blackrose', 'rainbowhib', 'moonflower', 'firelily', 'frostfern', 'thunderbloom'];
        const randomRare = rareSeeds[Math.floor(Math.random() * rareSeeds.length)];
        setPlantTypes(prev => ({ ...prev, [randomRare]: { ...prev[randomRare], unlocked: true } }));
        setRareCollection(prev => ({ ...prev, [randomRare]: true }));
        showNotification(`✨ RARE SEED FOUND! ${plantTypes[randomRare].name} unlocked!`);
      }
      
      setMoney(m => m + earn);
      setGreenhouses(prev => prev.map(gh => ({
        ...gh,
        pots: gh.pots.map(p => 
          p.id === potId ? { ...p, plant: null, water: 50, growth: 0, stage: 0, pest: null, fertilized: false, fertilizerTime: 0, growthStartTime: null } : p
        )
      })));
      
      // Update plant stats
      setPlantStats(prev => ({
        ...prev,
        [plantType]: {
          ...prev[plantType],
          sold: prev[plantType].sold + 1,
          earned: prev[plantType].earned + earn,
          fastestGrowth: growthTime && (!prev[plantType].fastestGrowth || growthTime < prev[plantType].fastestGrowth) 
            ? growthTime 
            : prev[plantType].fastestGrowth
        }
      }));
      
      setStats(p => ({ ...p, plantsSold: p.plantsSold + 1, totalEarned: p.totalEarned + earn }));
      setQuestProgress(prev => ({ 
        ...prev, 
        plantsHarvested: prev.plantsHarvested + 1,
        moneyEarned: prev.moneyEarned + earn
      }));

      // Track for challenges
      const speedrunChallenge = activeChallenges.find(c => c.id === 'speedrun' && c.active);
      if (speedrunChallenge) {
        setChallengeProgress(prev => ({
          ...prev,
          speedrun: (prev.speedrun || 0) + earn
        }));
      }
      
      const minimalistChallenge = activeChallenges.find(c => c.id === 'minimalist' && c.active);
      if (minimalistChallenge) {
        setChallengeProgress(prev => ({
          ...prev,
          minimalist: (prev.minimalist || 0) + earn
        }));
      }
      
      // Show combined bonus only when any bonus exists (event bonuses removed)
      if (mastery.bonus > 0 || auraBonus > 0 || globalAura > 0) {
        const bonusPercent = Math.round((mastery.bonus + auraBonus + globalAura) * 100);
        showNotification(`💰 +$${earn} (+${bonusPercent}% bonus!)`);
      }

      // Tutorial progress - advance to completion screen
      if (showTutorial && tutorialStep === 4) {
        setTimeout(() => setTutorialStep(5), 500);
      }
    }
  };

  const buyGreenhouseUpgrade = (greenhouseId, upgradeKey) => {
    const upgradeCosts = {
      dripirrigation: 500,
      humidifier: 300,
      growlight: 250,
      hvac: 400,
      climatecontrol: 600,
      advancedhvac: 800
    };
    
    const cost = upgradeCosts[upgradeKey];
    const greenhouse = greenhouses.find(gh => gh.id === greenhouseId);
    
    if (!greenhouse || greenhouse.upgrades[upgradeKey]) return;
    if (money < cost) { showNotification('❌ Not enough money!'); return; }
    
    setMoney(m => m - cost);
    setGreenhouses(prev => prev.map(gh => 
      gh.id === greenhouseId 
        ? { ...gh, upgrades: { ...gh.upgrades, [upgradeKey]: true } }
        : gh
    ));
    setStats(p => ({ ...p, upgradesPurchased: p.upgradesPurchased + 1 }));
    showNotification(`✓ Upgrade installed in ${greenhouse.name}!`);
  };

  const buyUpgrade = (key) => {
    const up = upgrades[key];
    if (money >= up.cost && (!up.purchased || up.consumable)) {
      setMoney(m => m - up.cost);
      if (up.consumable) {
        if (key === 'fliespack') { 
          setFlyInventory(f => f + 5); 
          showNotification('🪰 +5 Flies!'); 
        } else if (key === 'mysteryseed') {
          // 95% chance for rare, 5% for ultra rare
          const isUltraRare = Math.random() < 0.05;
          const rareSeeds = ['blackrose', 'rainbowhib', 'moonflower', 'firelily', 'frostfern', 'thunderbloom'];
          const ultraRareSeeds = ['crystalorchid'];
          const seedPool = isUltraRare ? ultraRareSeeds : rareSeeds;
          const randomSeed = seedPool[Math.floor(Math.random() * seedPool.length)];
          
          setPlantTypes(prev => ({ ...prev, [randomSeed]: { ...prev[randomSeed], unlocked: true } }));
          setRareCollection(prev => ({ ...prev, [randomSeed]: true }));
          
          if (isUltraRare) {
            showNotification(`✨✨ ULTRA RARE! ${plantTypes[randomSeed].name} unlocked!`);
          } else {
            showNotification(`✨ RARE! ${plantTypes[randomSeed].name} unlocked!`);
          }
        }
      } else {
        setUpgrades(p => ({ ...p, [key]: { ...up, purchased: true } }));
        const isAuto = ['dripirrigation', 'humidifier', 'growlight', 'hvac'].includes(key);
        setStats(p => ({ ...p, upgradesPurchased: p.upgradesPurchased + 1, automationPurchased: isAuto ? p.automationPurchased + 1 : p.automationPurchased }));
        
        if (key === 'greenhouse2') {
          setGreenhouses(prev => prev.map(gh => gh.id === 2 ? { ...gh, unlocked: true } : gh));
          setStats(p => ({ ...p, greenhousesOwned: 2 }));
          showNotification('🏛️ Greenhouse #2 Unlocked!');
        }
        if (key === 'greenhouse3') {
          setGreenhouses(prev => prev.map(gh => gh.id === 3 ? { ...gh, unlocked: true } : gh));
          setStats(p => ({ ...p, greenhousesOwned: 3 }));
          showNotification('🏛️ Greenhouse #3 Unlocked!');
        }
        
        const unlocks = { 
          peacelilyunlock: 'peacelily', 
          cactusunlock: 'cactus', 
          elephantearunlock: 'elephantear', 
          orchidunlock: 'orchid', 
          succulentunlock: 'succulent', 
          fernunlock: 'fern', 
          venustrapunlock: 'venustrap',
          blackroseunlock: 'blackrose',
          rainbowhibunlock: 'rainbowhib',
          crystalorchidunlock: 'crystalorchid',
          moonflowerunlock: 'moonflower',
          firelilyunlock: 'firelily',
          frostfernunlock: 'frostfern',
          thunderbloomunlock: 'thunderbloom'
        };
        if (unlocks[key]) {
          setPlantTypes(p => ({ ...p, [unlocks[key]]: { ...p[unlocks[key]], unlocked: true } }));
          setStats(p => ({ ...p, plantsUnlocked: p.plantsUnlocked + 1 }));
          if (unlocks[key] in rareCollection) {
            setRareCollection(prev => ({ ...prev, [unlocks[key]]: true }));
          }
        }
      }
    }
  };

  const adjustEnv = (greenhouseId, type, delta) => {
    setGreenhouses(prev => prev.map(gh => 
      gh.id === greenhouseId 
        ? { ...gh, environment: { ...gh.environment, [type]: Math.max(0, Math.min(100, gh.environment[type] + delta)) } }
        : gh
    ));
  };

  const getEmoji = (type, stage) => {
    if (!type) return '🪴';
    if (stage === 0) return '🌱';
    if (stage === 1) return '🌿';
    if (stage === 2) return '🪴';
    const map = { 
      hosta: '🌺', 
      peacelily: '🌸', 
      cactus: '🌵', 
      elephantear: '🌴', 
      orchid: '🌷', 
      succulent: '🌿', 
      fern: '🍀', 
      venustrap: '🪴',
      blackrose: '🌹',
      rainbowhib: '🌺',
      crystalorchid: '💎',
      goldenlotus: '🪷',
      moonflower: '🌙',
      firelily: '🔥',
      frostfern: '❄️',
      thunderbloom: '⚡'
    };
    return map[type] || '🌺';
  };

  const getRarityColor = (plantKey) => {
    const plant = plantTypes[plantKey];
    if (!plant) return '#334155';
    if (plant.rarity === 'legendary') return '#fbbf24';
    if (plant.rarity === 'ultra') return '#a855f7';
    if (plant.rarity === 'rare') return '#3b82f6';
    return '#334155';
  };

  const getRarityBadge = (plantKey) => {
    const plant = plantTypes[plantKey];
    if (!plant || plant.rarity === 'common') return null;
    if (plant.rarity === 'legendary') return '⭐⭐⭐⭐';
    if (plant.rarity === 'ultra') return '⭐⭐⭐';
    if (plant.rarity === 'rare') return '⭐⭐';
    return null;
  };

  const getHealth = (pot, greenhouse) => {
    if (!pot.plant) return '#666';
    const pt = plantTypes[pot.plant];
    let avg = ((100 - Math.abs(pot.water - pt.waterNeed)) + (100 - Math.abs(greenhouse.environment.light - pt.lightNeed)) + (100 - Math.abs(greenhouse.environment.temp - pt.tempNeed))) / 3;
    if (pot.pest) avg *= 0.5;
    return avg > 70 ? '#4ade80' : avg > 40 ? '#fbbf24' : '#f87171';
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const SmartUI = upgrades.smartphone.purchased;
  const currentGreenhouse = greenhouses.find(gh => gh.id === activeGreenhouse);

  const MainMenu = () => {
    const [showLoadGame, setShowLoadGame] = useState(false);
    const [showSettingsMenu, setShowSettingsMenu] = useState(false);

    const getSaveSlots = () => {
      const slots = [];
      for (let i = 1; i <= SAVE_SLOTS; i++) {
        const saveData = localStorage.getItem(`greenhouse_save_slot_${i}`);
        if (saveData) {
          try {
            const data = JSON.parse(saveData);
            slots.push({ slot: i, data, exists: true });
          } catch {
            slots.push({ slot: i, exists: false });
          }
        } else {
          slots.push({ slot: i, exists: false });
        }
      }
      return slots;
    };

    const [showNewGameSlotSelect, setShowNewGameSlotSelect] = useState(false);

    const startNewGame = () => {
      setShowNewGameSlotSelect(true);
  };

  const confirmNewGame = (slot) => {
    const saveData = localStorage.getItem(`greenhouse_save_slot_${slot}`);
    if (saveData) {
      if (window.confirm(`⚠️ Slot ${slot} already has a save game. Override it?`)) {
        // Clear the existing save
        localStorage.removeItem(`greenhouse_save_slot_${slot}`);
        setCurrentSaveSlot(slot);
        setGameState('playing');
        setShowNewGameSlotSelect(false);
      }
    } else {
      setCurrentSaveSlot(slot);
      setGameState('playing');
      setShowNewGameSlotSelect(false);
    }
  };

    const loadGame = (slot) => {
      const saveData = localStorage.getItem(`greenhouse_save_slot_${slot}`);
      if (saveData) {
        try {
          const data = JSON.parse(saveData);
          setMoney(data.money);
          setGreenhouses(data.greenhouses);
          setCurrentSeason(data.currentSeason);
          setSeasonTimeLeft(data.seasonTimeLeft);
          setUpgrades(data.upgrades);
          setPlantTypes(data.plantTypes);
          setFlyInventory(data.flyInventory);
          setStats(data.stats);
          setUnlockedAchievements(data.unlockedAchievements);
          setDailyReward(data.dailyReward || { lastClaimed: null, streakDay: 0, canClaim: false });
          setQuests(data.quests || { daily: [], weekly: [], permanent: [] });
          setPlantStats(data.plantStats || plantStats);
          setPrestigeLevel(data.prestigeLevel || 0);
          setQuestProgress(data.questProgress || questProgress);
          setRareCollection(data.rareCollection || rareCollection);
          setStoryQuests(data.storyQuests || storyQuests);
          setPermanentBonuses(data.permanentBonuses || { growthBonus: 0, allPlantsUnlocked: false });
          
          setActiveChallenges(data.activeChallenges || []);
          setChallengeProgress(data.challengeProgress || {});
          setCompletedChallenges(data.completedChallenges || []);
          setTutorialCompleted(data.tutorialCompleted || false);
          setCurrentSaveSlot(slot);
          setGameState('playing');
          showNotification(`Game loaded from Slot ${slot}!`);
        } catch (err) {
          showNotification('❌ Failed to load save file!');
        }
      }
    };

    const bgColor = darkMode ? '#0f172a' : '#1e293b';
    const cardColor = darkMode ? 'rgba(15, 23, 42, 0.9)' : 'rgba(30, 41, 59, 0.9)';

    return (
      <div style={{ width: '100%', height: '100vh', background: `linear-gradient(to bottom, ${bgColor}, #0f172a)`, display: 'flex', justifyContent: 'center', alignItems: 'center', fontFamily: 'monospace', color: '#e2e8f0' }}>
        <div style={{ textAlign: 'center', maxWidth: '500px', width: '100%', padding: '20px' }}>
          <div style={{ fontSize: '72px', marginBottom: '20px' }}>🪴</div>
          <h1 style={{ fontSize: '48px', margin: '0 0 10px 0', color: '#4ade80', textShadow: '0 0 20px rgba(74, 222, 128, 0.5)' }}>
            Sprout & Bloom
          </h1>
          <p style={{ fontSize: '18px', color: '#94a3b8', marginBottom: '40px' }}>
            Grow. Automate. Profit.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <button onClick={startNewGame} style={{ padding: '20px', background: '#4ade80', border: 'none', borderRadius: '12px', color: '#1e293b', cursor: 'pointer', fontSize: '20px', fontWeight: 'bold', transition: 'transform 0.2s', boxShadow: '0 4px 12px rgba(74, 222, 128, 0.3)' }} onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'} onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}>
              🆕 New Game
            </button>
            <button onClick={() => setShowLoadGame(true)} style={{ padding: '20px', background: '#3b82f6', border: 'none', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '20px', fontWeight: 'bold', transition: 'transform 0.2s', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }} onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'} onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}>
              💾 Load Game
            </button>
            <button onClick={() => setShowSettingsMenu(true)} style={{ padding: '20px', background: '#8b5cf6', border: 'none', borderRadius: '12px', color: 'white', cursor: 'pointer', fontSize: '20px', fontWeight: 'bold', transition: 'transform 0.2s', boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)' }} onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'} onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}>
              ⚙️ Settings
            </button>
          </div>
        </div>

        {showLoadGame && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ background: cardColor, padding: '30px', borderRadius: '12px', maxWidth: '600px', width: '90%', border: '2px solid #334155' }}>
              <h2 style={{ marginTop: 0, color: '#4ade80' }}>💾 Load Game</h2>
              {getSaveSlots().map(({ slot, data, exists }) => (
                <div key={slot} style={{ padding: '15px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '10px', cursor: exists ? 'pointer' : 'default', opacity: exists ? 1 : 0.5, transition: 'transform 0.2s' }} onClick={() => exists && loadGame(slot)} onMouseEnter={(e) => exists && (e.currentTarget.style.transform = 'scale(1.02)')} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                  <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px' }}>Slot {slot}</div>
                  {exists ? (
                    <div style={{ fontSize: '13px', color: '#94a3b8' }}>
                      💰 ${data.money} | 🏛️ {data.stats?.greenhousesOwned || 1} Greenhouses | 🔥 Day {data.dailyReward?.streakDay || 0} Streak
                      <br />
                      🌱 {data.stats?.plantsUnlocked || 1} Plants Unlocked
                    </div>
                  ) : (
                    <div style={{ fontSize: '13px', color: '#64748b' }}>Empty Slot</div>
                  )}
                </div>
              ))}
              <button onClick={() => setShowLoadGame(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Cancel</button>
            </div>
          </div>        

        )}

        {showNewGameSlotSelect && (
  <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
    <div style={{ background: cardColor, padding: '30px', borderRadius: '12px', maxWidth: '600px', width: '90%', border: '2px solid #334155' }}>
      <h2 style={{ marginTop: 0, color: '#4ade80' }}>🆕 Select Save Slot</h2>
      <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '20px' }}>Choose a slot for your new game:</p>
      {getSaveSlots().map(({ slot, data, exists }) => (
        <div key={slot} style={{ padding: '15px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '10px', cursor: 'pointer', transition: 'transform 0.2s', border: exists ? '2px solid #fbbf24' : '2px solid transparent' }} onClick={() => confirmNewGame(slot)} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
          <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Slot {slot}</span>
            {exists && <span style={{ fontSize: '12px', color: '#fbbf24', padding: '4px 8px', background: 'rgba(251, 191, 36, 0.2)', borderRadius: '4px' }}>⚠️ Has Save</span>}
          </div>
          {exists ? (
            <div style={{ fontSize: '13px', color: '#94a3b8' }}>
              💰 ${data.money} | 🏛️ {data.stats?.greenhousesOwned || 1} Greenhouses | 🔥 Day {data.dailyReward?.streakDay || 0} Streak
              <br />
              🌱 {data.stats?.plantsUnlocked || 1} Plants Unlocked
            </div>
          ) : (
            <div style={{ fontSize: '13px', color: '#64748b' }}>Empty Slot - Click to start new game</div>
          )}
        </div>
      ))}
      <button onClick={() => setShowNewGameSlotSelect(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Cancel</button>
    </div>
  </div>
)}

        {showSettingsMenu && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }} onClick={() => setShowSettingsMenu(false)}>
            <div style={{ background: cardColor, padding: '30px', borderRadius: '12px', maxWidth: '500px', width: '90%', border: '2px solid #334155' }} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ marginTop: 0, color: '#4ade80' }}>⚙️ Settings</h2>
              
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', color: '#fbbf24' }}>Sound</h3>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '14px', display: 'block', marginBottom: '5px' }}>Master Volume: {soundSettings.masterVolume}%</label>
                  <input type="range" min="0" max="100" value={soundSettings.masterVolume} onChange={(e) => setSoundSettings(prev => ({ ...prev, masterVolume: parseInt(e.target.value) }))} style={{ width: '100%' }} />
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ fontSize: '14px', display: 'block', marginBottom: '5px' }}>SFX Volume: {soundSettings.sfxVolume}%</label>
                  <input type="range" min="0" max="100" value={soundSettings.sfxVolume} onChange={(e) => setSoundSettings(prev => ({ ...prev, sfxVolume: parseInt(e.target.value) }))} style={{ width: '100%' }} />
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '16px', color: '#fbbf24' }}>Display</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={darkMode} onChange={(e) => setDarkMode(e.target.checked)} />
                  <span style={{ fontSize: '14px' }}>Dark Mode</span>
                </label>
              </div>

              <button onClick={() => setShowSettingsMenu(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (gameState === 'menu') {
    return <MainMenu />;
  }

  const bgColor = darkMode ? '#0f172a' : '#1e293b';

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: bgColor, fontFamily: 'monospace', color: '#e2e8f0', display: 'flex' }}>
      {notification && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', background: '#1e293b', padding: '15px 20px', borderRadius: '8px', border: '2px solid #4ade80', zIndex: 200, boxShadow: '0 4px 12px rgba(0,0,0,0.5)', fontSize: '14px' }}>
          {notification}
        </div>
      )}

      <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '15px', background: 'rgba(30, 41, 59, 0.95)', borderRadius: '12px', border: '2px solid #334155', position: 'relative' }}>
          {showSaveIndicator && (
            <div style={{ position: 'absolute', top: '-10px', right: '10px', background: '#10b981', padding: '6px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.5)' }}>
              💾 Saved
            </div>
          )}
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#10b981' }}>💰 ${money}</div>
            <div style={{ fontSize: '14px', color: '#94a3b8' }}>🪰 Flies: {flyInventory}</div>
            
            {prestigeLevel > 0 && (
              <div style={{ fontSize: '13px', padding: '6px 12px', background: prestigeLevel >= 10 ? '#a855f7' : prestigeLevel >= 5 ? '#fbbf24' : prestigeLevel >= 3 ? '#94a3b8' : '#cd7f32', borderRadius: '6px', fontWeight: 'bold', color: 'white' }}>
                {prestigeLevel >= 10 ? '💎' : prestigeLevel >= 5 ? '🥇' : prestigeLevel >= 3 ? '🥈' : '🥉'} P{prestigeLevel}
              </div>
            )}
            {(dailyReward.streakDay >= 1 || pendingDailyReward) && (
                <div style={{ fontSize: '14px', padding: '6px 12px', background: '#ef4444', borderRadius: '6px', fontWeight: 'bold', color: 'white', cursor: 'pointer' }} onClick={() => setShowStreakRewards(true)} title="View Streak Rewards">
                  🔥 Day {pendingDailyReward?.streakDay || dailyReward.streakDay} Streak
                </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <div style={{ fontSize: '16px', padding: '8px 12px', background: SEASONS[currentSeason].color, color: '#1e293b', borderRadius: '6px', fontWeight: 'bold' }}>
              {SEASONS[currentSeason].icon} {SEASONS[currentSeason].name} ({formatTime(seasonTimeLeft)})
            </div>
            <button onClick={() => saveGame(currentSaveSlot, true)} style={{ padding: '8px 16px', background: '#10b981', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold' }}>
              💾 Save
            </button>
            <button onClick={() => setShowAlmanac(true)} style={{ padding: '8px 16px', background: 'linear-gradient(135deg, #059669, #10b981)', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 'bold', boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)' }}>
              📗 Gardener's Almanac
            </button>
            <button onClick={() => setShowShop(true)} style={{ padding: '8px 16px', background: '#8b5cf6', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ShoppingCart size={16} /> Shop
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', overflowX: 'auto', padding: '5px' }}>
          {greenhouses.filter(gh => gh.unlocked).map(gh => (
            <button key={gh.id} onClick={() => setActiveGreenhouse(gh.id)} style={{ padding: '12px 20px', background: activeGreenhouse === gh.id ? '#4ade80' : '#334155', border: 'none', borderRadius: '8px', color: activeGreenhouse === gh.id ? '#1e293b' : 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Home size={16} /> {gh.name}
            </button>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          {currentGreenhouse?.pots.map(pot => {
            const rarityBadge = pot.plant ? getRarityBadge(pot.plant) : null;
            const rarityColor = pot.plant ? getRarityColor(pot.plant) : '#334155';
            
            return (
              <div key={pot.id} style={{ padding: '20px', background: 'rgba(30, 41, 59, 0.95)', borderRadius: '12px', border: `3px solid ${pot.plant && plantTypes[pot.plant]?.rarity !== 'common' ? rarityColor : getHealth(pot, currentGreenhouse)}`, position: 'relative', boxShadow: pot.plant && plantTypes[pot.plant]?.rarity !== 'common' ? `0 0 20px ${rarityColor}50` : 'none' }}>
                <div style={{ textAlign: 'center', fontSize: '64px', marginBottom: '10px' }}>{getEmoji(pot.plant, pot.stage)}</div>
                
                {rarityBadge && (
                <div style={{ position: 'absolute', top: '10px', right: '10px', background: rarityColor, padding: '4px 8px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', color: 'white' }}>
                  {rarityBadge}
                </div>
              )}
              
              {pot.fertilized && (
                <div style={{ position: 'absolute', top: rarityBadge ? '45px' : '10px', right: '10px', background: '#22c55e', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                  🌿 {Math.ceil(pot.fertilizerTime / 1000)}s
                </div>
              )}
              
              {pot.pest && (
                <div style={{ position: 'absolute', top: '10px', left: '10px', background: '#ef4444', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                  {pot.pest === 'fly' ? '🪰 Flies!' : '🦟 Aphids!'}
                </div>
              )}
              
              {pot.plant ? (
                <>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', textAlign: 'center' }}>
                    {plantTypes[pot.plant].name}
                  </div>
                  <div style={{ fontSize: '13px', color: '#a855f7', fontWeight: 'bold', textAlign: 'center', marginBottom: '10px' }}>
                    Stage: {pot.stage + 1}/4
                  </div>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                    💧 Water: {Math.round(pot.water)}%
                  </div>
                  <div style={{ height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden', marginBottom: '8px' }}>
                    <div style={{ width: `${pot.water}%`, height: '100%', background: '#3b82f6' }} />
                  </div>
                  {pot.stage < 3 && (
                    <>
                      <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px' }}>
                        🌱 Growth: {Math.round((pot.growth / plantTypes[pot.plant].growthTime) * 100)}%
                      </div>
                      <div style={{ height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
                        <div style={{ width: `${(pot.growth / plantTypes[pot.plant].growthTime) * 100}%`, height: '100%', background: '#4ade80' }} />
                      </div>
                    </>
                  )}
                  {pot.stage === 3 && (
                    <div style={{ fontSize: '13px', color: '#4ade80', fontWeight: 'bold', textAlign: 'center', marginBottom: '12px' }}>
                      ✓ Ready to Harvest!
                    </div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                    <button onClick={() => waterPlant(pot.id)} style={{ flex: '1 1 45%', padding: '6px', background: '#0ea5e9', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '11px' }}>💧 Water</button>
                    {upgrades.fertilizer.purchased && !pot.fertilized && <button onClick={() => fertilizePlant(pot.id)} style={{ flex: '1 1 45%', padding: '6px', background: '#22c55e', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '11px' }}>🌿 Fertilize</button>}
                    {pot.pest && <button onClick={() => removePest(pot.id, pot.pest)} style={{ flex: '1 1 45%', padding: '6px', background: '#ef4444', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '11px' }}>{pot.pest === 'fly' ? '🪰 Swat' : '🦟 Spray'}</button>}
                    {plantTypes[pot.plant].feedsOnFlies && <button onClick={() => feedVenusTrap(pot.id)} disabled={flyInventory < 1} style={{ flex: '1 1 45%', padding: '6px', background: flyInventory < 1 ? '#64748b' : '#a855f7', border: 'none', borderRadius: '6px', color: 'white', cursor: flyInventory < 1 ? 'not-allowed' : 'pointer', fontSize: '11px' }}>🪰 Feed</button>}
                    {pot.stage === 3 && <button onClick={() => harvestPlant(pot.id)} style={{ flex: '1 1 100%', padding: '8px', background: '#10b981', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}>💰 Sell ${plantTypes[pot.plant].sellPrice}</button>}
                  </div>
                </>
              ) : (
                <button onClick={() => setShowPlantMenu(pot.id)} style={{ width: '100%', padding: '12px', background: '#8b5cf6', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                  🌱 Plant Seed
                </button>
              )}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ width: SmartUI ? '340px' : '320px', background: SmartUI ? 'linear-gradient(135deg, #1e293b, #334155)' : 'rgba(30, 41, 59, 0.95)', borderLeft: SmartUI ? '3px solid #3b82f6' : '2px solid #334155', padding: '20px', overflowY: 'auto' }}>
        {SmartUI && <div style={{ textAlign: 'center', marginBottom: '20px', padding: '10px', background: 'rgba(59, 130, 246, 0.2)', borderRadius: '8px' }}><Smartphone size={24} style={{ color: '#3b82f6', marginBottom: '5px' }} /><div style={{ fontSize: '12px', color: '#93c5fd' }}>Smart Control System</div></div>}
        
        <div style={{ marginBottom: '20px', padding: '12px', background: 'rgba(139, 92, 246, 0.2)', borderRadius: '8px', border: '1px solid rgba(139, 92, 246, 0.4)' }}>
          <h3 style={{ fontSize: '16px', margin: '0 0 10px 0', color: '#a855f7' }}>{currentGreenhouse?.name}</h3>
          <button onClick={() => setShowGreenhouseUpgrades(activeGreenhouse)} style={{ width: '100%', padding: '8px', background: '#8b5cf6', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>⚙️ Greenhouse Upgrades</button>
        </div>

        <h2 style={{ fontSize: '18px', marginBottom: '15px', color: '#4ade80' }}>🌡️ Environment</h2>
        {['temp', 'humidity', 'light'].map(type => (
          <div key={type} style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {type === 'temp' && <Sun size={16} color="#f59e0b" />}
                {type === 'humidity' && <Droplet size={16} color="#3b82f6" />}
                {type === 'light' && <Zap size={16} color="#eab308" />}
                <span style={{ fontSize: '13px' }}>{type === 'temp' ? 'Temperature' : type === 'humidity' ? 'Humidity' : 'Light'}</span>
              </div>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: type === 'temp' ? '#fbbf24' : type === 'humidity' ? '#3b82f6' : '#eab308' }}>{Math.round(currentGreenhouse?.environment[type] || 0)}{type === 'temp' ? '°F' : '%'}</span>
            </div>
            {SmartUI ? (
              <input type="range" min="0" max="100" value={currentGreenhouse?.environment[type] || 0} onChange={(e) => adjustEnv(activeGreenhouse, type, parseInt(e.target.value) - (currentGreenhouse?.environment[type] || 0))} style={{ width: '100%' }} />
            ) : (
              <div style={{ display: 'flex', gap: '4px' }}>
                <button onClick={() => adjustEnv(activeGreenhouse, type, -5)} style={{ padding: '6px 12px', background: '#475569', border: 'none', borderRadius: '4px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>-</button>
                <div style={{ flex: 1, height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden', alignSelf: 'center' }}>
                  <div style={{ width: `${currentGreenhouse?.environment[type] || 0}%`, height: '100%', background: type === 'temp' ? '#f59e0b' : type === 'humidity' ? '#3b82f6' : '#eab308' }} />
                </div>
                <button onClick={() => adjustEnv(activeGreenhouse, type, 5)} style={{ padding: '6px 12px', background: '#475569', border: 'none', borderRadius: '4px', color: 'white', cursor: 'pointer', fontSize: '12px' }}>+</button>
              </div>
            )}
          </div>
        ))}
        
        <div style={{ marginTop: '30px', padding: '12px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
          <h3 style={{ fontSize: '16px', margin: '0 0 10px 0', color: '#4ade80' }}>Active Systems</h3>
          <div style={{ fontSize: '11px', color: '#94a3b8' }}>
            {currentGreenhouse?.upgrades.dripirrigation && <div>✓ Drip Irrigation</div>}
            {currentGreenhouse?.upgrades.humidifier && <div>✓ Humidifier</div>}
            {currentGreenhouse?.upgrades.growlight && <div>✓ Grow Light</div>}
            {currentGreenhouse?.upgrades.hvac && <div>✓ HVAC System</div>}
            {currentGreenhouse?.upgrades.climatecontrol && <div>✓ Climate Control</div>}
            {currentGreenhouse?.upgrades.advancedhvac && <div>✓ Advanced HVAC</div>}
            {upgrades.smartphone.purchased && <div>✓ Smart Controls</div>}
            {!Object.values(currentGreenhouse?.upgrades || {}).some(u => u) && !upgrades.smartphone.purchased && <div>No automation yet</div>}
          </div>
        </div>

        <div style={{ marginTop: '20px', padding: '12px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
          <h3 style={{ fontSize: '16px', margin: '0 0 8px 0', color: '#fbbf24' }}>Season Effects</h3>
          <div style={{ fontSize: '11px', color: '#94a3b8', lineHeight: '1.6' }}>
            {currentSeason === 'spring' && <div>🌸 All plants +10% growth</div>}
            {currentSeason === 'summer' && <div>☀️ Desert plants +20%<br/>Tropical plants +15%</div>}
            {currentSeason === 'fall' && <div>🍂 Balanced conditions</div>}
            {currentSeason === 'winter' && <div>❄️ All plants -20%<br/>Tropical plants -30%</div>}
          </div>
        </div>
      </div>

      {showShop && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto', border: '2px solid #334155' }}>
            <h2 style={{ marginTop: 0, color: '#4ade80' }}>🛒 Shop & Upgrades</h2>
            
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#fbbf24', marginBottom: '10px' }}>Plant Seeds</h3>
              {Object.entries(upgrades).filter(([k]) => k.includes('unlock') && upgrades[k].category !== 'rare').map(([key, upgrade]) => (
                <div key={key} style={{ padding: '12px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: upgrade.purchased ? 0.5 : 1 }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{upgrade.name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{upgrade.effect}</div>
                  </div>
                  <button onClick={() => buyUpgrade(key)} disabled={upgrade.purchased || money < upgrade.cost} style={{ padding: '8px 16px', background: upgrade.purchased ? '#475569' : (money >= upgrade.cost ? '#8b5cf6' : '#64748b'), border: 'none', borderRadius: '6px', color: 'white', cursor: upgrade.purchased || money < upgrade.cost ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    {upgrade.purchased ? 'Owned' : `$${upgrade.cost}`}
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#fbbf24', marginBottom: '10px' }}>Tools</h3>
              {Object.entries(upgrades).filter(([k]) => !k.includes('unlock') && k !== 'fliespack' && !k.includes('greenhouse') && !['dripirrigation','humidifier','growlight','hvac','climatecontrol','advancedhvac','smartphone'].includes(k)).map(([key, upgrade]) => (
                <div key={key} style={{ padding: '12px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: upgrade.purchased ? 0.5 : 1 }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{upgrade.name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{upgrade.effect}</div>
                  </div>
                  <button onClick={() => buyUpgrade(key)} disabled={upgrade.purchased || money < upgrade.cost} style={{ padding: '8px 16px', background: upgrade.purchased ? '#475569' : (money >= upgrade.cost ? '#10b981' : '#64748b'), border: 'none', borderRadius: '6px', color: 'white', cursor: upgrade.purchased || money < upgrade.cost ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    {upgrade.purchased ? 'Owned' : `$${upgrade.cost}`}
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#fbbf24', marginBottom: '10px' }}>Global Automation</h3>
              {Object.entries(upgrades).filter(([k]) => k === 'smartphone' || k === 'autoharvester' || k === 'precisionfert').map(([key, upgrade]) => (
                <div key={key} style={{ padding: '12px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: upgrade.purchased ? 0.5 : 1 }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{upgrade.name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{upgrade.effect}</div>
                  </div>
                  <button onClick={() => buyUpgrade(key)} disabled={upgrade.purchased || money < upgrade.cost} style={{ padding: '8px 16px', background: upgrade.purchased ? '#475569' : (money >= upgrade.cost ? '#10b981' : '#64748b'), border: 'none', borderRadius: '6px', color: 'white', cursor: upgrade.purchased || money < upgrade.cost ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    {upgrade.purchased ? 'Owned' : `$${upgrade.cost}`}
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '20px' }}>
              
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#fbbf24', marginBottom: '10px' }}>Consumables</h3>
              {Object.entries(upgrades).filter(([k]) => k === 'fliespack' || k === 'mysteryseed').map(([key, upgrade]) => (
                <div key={key} style={{ padding: '12px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{upgrade.name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{upgrade.effect}</div>
                  </div>
                  <button onClick={() => buyUpgrade(key)} disabled={money < upgrade.cost} style={{ padding: '8px 16px', background: money >= upgrade.cost ? '#f59e0b' : '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: money < upgrade.cost ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    ${upgrade.cost}
                  </button>
                </div>
              ))}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '16px', color: '#3b82f6', marginBottom: '10px' }}>✨ Rare Plants</h3>
              {Object.entries(upgrades).filter(([k]) => k.includes('unlock') && upgrades[k].category === 'rare').map(([key, upgrade]) => (
                <div key={key} style={{ padding: '12px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: upgrade.purchased ? 0.5 : 1, border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#60a5fa' }}>{upgrade.name}</div>
                    <div style={{ fontSize: '11px', color: '#93c5fd' }}>{upgrade.effect}</div>
                  </div>
                  <button onClick={() => buyUpgrade(key)} disabled={upgrade.purchased || money < upgrade.cost} style={{ padding: '8px 16px', background: upgrade.purchased ? '#475569' : (money >= upgrade.cost ? '#3b82f6' : '#64748b'), border: 'none', borderRadius: '6px', color: 'white', cursor: upgrade.purchased || money < upgrade.cost ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    {upgrade.purchased ? 'Owned' : `$${upgrade.cost}`}
                  </button>
                </div>
              ))}
            </div>

            <button onClick={() => setShowShop(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close Shop</button>
          </div>
        </div>
      )}

      {showGreenhouseUpgrades !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto', border: '2px solid #334155' }}>
            <h2 style={{ marginTop: 0, color: '#4ade80' }}>⚙️ {greenhouses.find(gh => gh.id === showGreenhouseUpgrades)?.name} Upgrades</h2>
            
            {[
              { key: 'dripirrigation', name: 'Drip Irrigation', cost: 500, effect: 'Auto-water every 5s' },
              { key: 'humidifier', name: 'Humidifier', cost: 300, effect: 'Humidity +1/s' },
              { key: 'growlight', name: 'Grow Light', cost: 250, effect: 'Light +1/s' },
              { key: 'hvac', name: 'HVAC System', cost: 400, effect: 'Auto temp control' },
              { key: 'climatecontrol', name: 'Climate Control', cost: 600, effect: '-50% season impact' },
              { key: 'advancedhvac', name: 'Advanced HVAC', cost: 800, effect: 'Negate seasons completely' }
            ].map(upgrade => {
              const greenhouse = greenhouses.find(gh => gh.id === showGreenhouseUpgrades);
              const owned = greenhouse?.upgrades[upgrade.key];
              return (
                <div key={upgrade.key} style={{ padding: '12px', background: 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', opacity: owned ? 0.5 : 1 }}>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px' }}>{upgrade.name}</div>
                    <div style={{ fontSize: '11px', color: '#94a3b8' }}>{upgrade.effect}</div>
                  </div>
                  <button onClick={() => buyGreenhouseUpgrade(showGreenhouseUpgrades, upgrade.key)} disabled={owned || money < upgrade.cost} style={{ padding: '8px 16px', background: owned ? '#475569' : (money >= upgrade.cost ? '#10b981' : '#64748b'), border: 'none', borderRadius: '6px', color: 'white', cursor: owned || money < upgrade.cost ? 'not-allowed' : 'pointer', fontSize: '13px' }}>
                    {owned ? 'Owned' : `$${upgrade.cost}`}
                  </button>
                </div>
              );
            })}

            <button onClick={() => setShowGreenhouseUpgrades(null)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close</button>
          </div>
        </div>
      )}

      {showPlantMenu !== null && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', border: '2px solid #334155', maxHeight: '80vh', overflowY: 'auto' }}>
            <h3 style={{ marginTop: 0, color: '#4ade80' }}>Select a Plant</h3>
            {Object.entries(plantTypes).filter(([_, p]) => p.unlocked).map(([key, plant]) => (
              <button key={key} onClick={() => plantSeed(showPlantMenu, key)} disabled={money < plant.cost} style={{ display: 'block', width: '300px', padding: '12px', marginBottom: '10px', background: money >= plant.cost ? '#8b5cf6' : '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: money >= plant.cost ? 'pointer' : 'not-allowed', textAlign: 'left' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{plant.name}</div>
                <div style={{ fontSize: '12px', opacity: 0.8, marginBottom: '4px' }}>
                  Cost: ${plant.cost} | Sell: ${plant.sellPrice}
                </div>
                <div style={{ fontSize: '10px', opacity: 0.7 }}>
                  Water: {plant.waterNeed}% | Light: {plant.lightNeed}% | Temp: {plant.tempNeed}°F
                </div>
                <div style={{ fontSize: '10px', opacity: 0.7, marginTop: '4px', padding: '4px', background: 'rgba(0,0,0,0.3)', borderRadius: '4px' }}>
                  Climate: {plant.climate} | Best: {plant.preferredSeason}
                </div>
              </button>
            ))}
            <button onClick={() => setShowPlantMenu(null)} style={{ width: '100%', padding: '10px', marginTop: '10px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer' }}>Cancel</button>
          </div>
        </div>
      )}

      {showAchievements && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', maxWidth: '500px', maxHeight: '80vh', overflowY: 'auto', border: '2px solid #334155' }}>
            <h2 style={{ marginTop: 0, color: '#4ade80', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Award size={24} /> Achievements
            </h2>
            <div style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '20px' }}>
              Unlocked: {unlockedAchievements.length} / {ACHIEVEMENTS.length}
            </div>
            {ACHIEVEMENTS.map(achievement => {
              const unlocked = unlockedAchievements.includes(achievement.id);
              const isHidden = achievement.hidden && !unlocked;
              return (
                <div key={achievement.id} style={{ padding: '12px', background: unlocked ? 'rgba(16, 185, 129, 0.2)' : 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '10px', border: unlocked ? '2px solid #10b981' : '2px solid transparent' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ fontSize: '32px', opacity: unlocked ? 1 : 0.3 }}>{isHidden ? '❓' : achievement.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '14px', color: unlocked ? '#10b981' : '#cbd5e1' }}>
                        {isHidden ? '??? - Hidden Achievement' : achievement.name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8' }}>{isHidden ? 'Complete secret requirement to unlock' : achievement.desc}</div>
                    </div>
                    {unlocked && <div style={{ fontSize: '20px', color: '#10b981' }}>✓</div>}
                  </div>
                </div>
              );
            })}
            {canPrestige() && (
              <div style={{ marginTop: '20px', padding: '15px', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2), rgba(168, 85, 247, 0.2))', borderRadius: '8px', border: '2px solid #8b5cf6' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#a855f7', marginBottom: '8px' }}>🔄 Ready to Prestige!</div>
                <div style={{ fontSize: '12px', color: '#cbd5e1', marginBottom: '12px' }}>
                  Reset your progress for permanent bonuses and exclusive rewards!
                </div>
                <button onClick={() => { setShowAchievements(false); setShowPrestigeModal(true); }} style={{ width: '100%', padding: '10px', background: '#8b5cf6', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>
                  View Prestige Options
                </button>
              </div>
            )}
            <button onClick={() => setShowAchievements(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close</button>
          </div>
        </div>
      )}

      {/* Streak Rewards Modal */}
      {showStreakRewards && (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 200 }}>
      <div style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', padding: '30px', borderRadius: '16px', maxWidth: '500px', width: '90%', border: '3px solid #ef4444', boxShadow: '0 8px 32px rgba(239, 68, 68, 0.5)' }}>
      <h2 style={{ marginTop: 0, color: '#ef4444', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
        🔥 Daily Login Streak
      </h2>
      <p style={{ fontSize: '14px', color: '#94a3b8', marginBottom: '25px' }}>
        Current Streak: <strong style={{ color: '#ef4444', fontSize: '18px' }}>Day {dailyReward.streakDay}</strong>
        {stats.dailyStreakRecord > 0 && (
          <span style={{ marginLeft: '10px', color: '#fbbf24' }}>
            (Best: {stats.dailyStreakRecord} days)
          </span>
        )}
      </p>

{/* Daily Reward Popup */}
      {showDailyRewardPopup && pendingDailyReward && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 201 }}>
          <div style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', padding: '40px', borderRadius: '16px', maxWidth: '450px', border: '3px solid #fbbf24', boxShadow: '0 8px 32px rgba(251, 191, 36, 0.5)', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎁</div>
            <h2 style={{ marginTop: 0, marginBottom: '10px', color: '#fbbf24', fontSize: '28px' }}>Daily Reward!</h2>
            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ef4444', marginBottom: '20px' }}>
              🔥 Day {pendingDailyReward.streakDay} Streak
            </div>
            
            {pendingDailyReward.comebackBonus && (
              <div style={{ padding: '15px', background: 'rgba(16, 185, 129, 0.2)', borderRadius: '8px', marginBottom: '20px', border: '2px solid #10b981' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981', marginBottom: '5px' }}>
                  🎉 Welcome Back Bonus!
                </div>
                <div style={{ fontSize: '14px', color: '#6ee7b7' }}>
                  You've been away for a while. Here's $50 to get you started again!
                </div>
              </div>
            )}
            
            <div style={{ padding: '20px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '12px', border: '2px solid #fbbf24', marginBottom: '25px' }}>
              <div style={{ fontSize: '16px', color: '#94a3b8', marginBottom: '10px' }}>Today's Reward:</div>
              <div style={{ fontSize: '32px', marginBottom: '10px' }}>
                {pendingDailyReward.type === 'money' && '💰'}
                {pendingDailyReward.type === 'flies' && '🪰'}
                {pendingDailyReward.type === 'seed' && '🌱'}
                {pendingDailyReward.type === 'fertilizer' && '✨'}
                {pendingDailyReward.type === 'jackpot' && '🎁'}
              </div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fbbf24' }}>
                {pendingDailyReward.display}
              </div>
            </div>
            
            <button 
              onClick={claimDailyReward}
              style={{ 
                width: '100%', 
                padding: '15px', 
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)', 
                border: 'none', 
                borderRadius: '8px', 
                color: '#1e293b', 
                cursor: 'pointer', 
                fontSize: '18px', 
                fontWeight: 'bold',
                boxShadow: '0 4px 12px rgba(251, 191, 36, 0.4)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              Claim Reward! 🎁
            </button>
          </div>
        </div>
      )}


      <div style={{ marginBottom: '20px' }}>
        {[1, 2, 3, 4, 5, 6, 7].map(day => {
          const reward = DAILY_REWARDS[day - 1];
          const isCurrentDay = dailyReward.streakDay === day;
          const isCompleted = dailyReward.streakDay > day;

          return (
            <div 
              key={day} 
              style={{ 
                padding: '15px', 
                background: isCurrentDay ? 'rgba(239, 68, 68, 0.2)' : isCompleted ? 'rgba(16, 185, 129, 0.1)' : 'rgba(51, 65, 85, 0.5)',
                borderRadius: '8px', 
                marginBottom: '10px',
                border: isCurrentDay ? '2px solid #ef4444' : isCompleted ? '2px solid #10b981' : '2px solid transparent',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    background: isCurrentDay ? '#ef4444' : isCompleted ? '#10b981' : '#475569',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px',
                    fontWeight: 'bold',
                    color: 'white'
                  }}>
                    {isCompleted ? '✓' : day}
                  </div>
                  <div>
                    <div style={{ fontWeight: 'bold', fontSize: '14px', color: isCurrentDay ? '#ef4444' : '#e2e8f0' }}>
                      Day {day}
                      {isCurrentDay && <span style={{ marginLeft: '8px', fontSize: '12px', color: '#fbbf24' }}>← You are here</span>}
                    </div>
                    <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                      {reward.type === 'money' && `$${reward.amount}`}
                      {reward.type === 'seed' && 'Random Seed'}
                      {reward.type === 'flies' && `${reward.amount} Flies`}
                      {reward.type === 'fertilizer' && 'Fertilizer Boost'}
                      {reward.type === 'jackpot' && `$${reward.amount} + Seed`}
                    </div>
                  </div>
                </div>
                {isCompleted && (
                  <div style={{ fontSize: '24px', color: '#10b981' }}>✓</div>
                )}
                {isCurrentDay && (
                  <div style={{ fontSize: '24px' }}>🔥</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: '15px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px', border: '1px solid rgba(251, 191, 36, 0.3)', marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: '#fbbf24', lineHeight: '1.6' }}>
          💡 <strong>Tip:</strong> Log in every day to maintain your streak! Missing a day resets your progress back to Day 1.
        </div>
      </div>

      <button 
        onClick={() => setShowStreakRewards(false)} 
        style={{ 
          width: '100%', 
          padding: '12px', 
          background: '#64748b', 
          border: 'none', 
          borderRadius: '8px', 
          color: 'white', 
          cursor: 'pointer', 
          fontSize: '14px',
          fontWeight: 'bold'
        }}
      >
        Close
      </button>
    </div>
  </div>
)}

      {showQuestsModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto', border: '2px solid #334155', width: '90%' }}>
            <h2 style={{ marginTop: 0, color: '#4ade80', display: 'flex', alignItems: 'center', gap: '10px' }}>
              📋 Quests & Missions
            </h2>
            
            {/* Daily Quests */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ fontSize: '18px', color: '#3b82f6', marginBottom: '15px' }}>🌅 Daily Quests</h3>
              {quests.daily.map(quest => {
                const progress = Math.min(quest.progress, quest.goal);
                const percentage = (progress / quest.goal) * 100;
                const completed = progress >= quest.goal;
                return (
                  <div key={quest.id} style={{ padding: '15px', background: completed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '12px', border: completed ? '2px solid #10b981' : '2px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>{quest.icon}</span>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '14px', color: completed ? '#10b981' : '#e2e8f0' }}>
                            {quest.name} {completed && '✓'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                            Reward: {quest.reward.type === 'money' ? `$${quest.reward.amount}` : `${quest.reward.amount} flies`}
                          </div>
                        </div>
                      </div>
                      {completed && !quest.claimed && (
                        <button onClick={() => claimQuestReward('daily', quest.id)} style={{ padding: '8px 16px', background: '#10b981', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                          Claim!
                        </button>
                      )}
                      {quest.claimed && (
                        <div style={{ padding: '8px 16px', background: '#64748b', borderRadius: '6px', fontSize: '13px', color: 'white' }}>
                          Claimed
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${percentage}%`, height: '100%', background: completed ? '#10b981' : '#3b82f6', transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', minWidth: '60px', textAlign: 'right' }}>
                        {progress}/{quest.goal}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Weekly Quests */}
            <div style={{ marginBottom: '25px' }}>
              <h3 style={{ fontSize: '18px', color: '#8b5cf6', marginBottom: '15px' }}>📅 Weekly Quests</h3>
              {quests.weekly.map(quest => {
                const progress = Math.min(quest.progress, quest.goal);
                const percentage = (progress / quest.goal) * 100;
                const completed = progress >= quest.goal;
                return (
                  <div key={quest.id} style={{ padding: '15px', background: completed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '12px', border: completed ? '2px solid #10b981' : '2px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>{quest.icon}</span>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '14px', color: completed ? '#10b981' : '#e2e8f0' }}>
                            {quest.name} {completed && '✓'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                            Reward: {quest.reward.type === 'money' ? `$${quest.reward.amount}` : 'Random Seed'}
                          </div>
                        </div>
                      </div>
                      {completed && !quest.claimed && (
                        <button onClick={() => claimQuestReward('weekly', quest.id)} style={{ padding: '8px 16px', background: '#10b981', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                          Claim!
                        </button>
                      )}
                      {quest.claimed && (
                        <div style={{ padding: '8px 16px', background: '#64748b', borderRadius: '6px', fontSize: '13px', color: 'white' }}>
                          Claimed
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${percentage}%`, height: '100%', background: completed ? '#10b981' : '#8b5cf6', transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', minWidth: '80px', textAlign: 'right' }}>
                        {progress}/{quest.goal}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Permanent Quests */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ fontSize: '18px', color: '#f59e0b', marginBottom: '15px' }}>🏆 Milestones</h3>
              {quests.permanent.map(quest => {
                const progress = Math.min(quest.progress, quest.goal);
                const percentage = (progress / quest.goal) * 100;
                const completed = progress >= quest.goal;
                return (
                  <div key={quest.id} style={{ padding: '15px', background: completed ? 'rgba(16, 185, 129, 0.1)' : 'rgba(51, 65, 85, 0.5)', borderRadius: '8px', marginBottom: '12px', border: completed ? '2px solid #10b981' : '2px solid transparent' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '24px' }}>{quest.icon}</span>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '14px', color: completed ? '#10b981' : '#e2e8f0' }}>
                            {quest.name} {completed && '✓'}
                          </div>
                          <div style={{ fontSize: '12px', color: '#94a3b8' }}>
                            Reward: ${quest.reward.amount}
                          </div>
                        </div>
                      </div>
                      {completed && !quest.claimed && (
                        <button onClick={() => claimQuestReward('permanent', quest.id)} style={{ padding: '8px 16px', background: '#10b981', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold' }}>
                          Claim!
                        </button>
                      )}
                      {quest.claimed && (
                        <div style={{ padding: '8px 16px', background: '#64748b', borderRadius: '6px', fontSize: '13px', color: 'white' }}>
                          Claimed
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{ flex: 1, height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ width: `${percentage}%`, height: '100%', background: completed ? '#10b981' : '#f59e0b', transition: 'width 0.3s' }} />
                      </div>
                      <div style={{ fontSize: '12px', color: '#94a3b8', minWidth: '80px', textAlign: 'right' }}>
                        {progress}/{quest.goal}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <button onClick={() => setShowQuestsModal(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close</button>
          </div>
        </div>
      )}

      {/* Plant Codex Modal */}
      {showCodex && !selectedCodexPlant && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', border: '2px solid #22c55e', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto' }}>
            <h2 style={{ marginTop: 0, color: '#22c55e', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              🌿 Plant Codex 🌿
            </h2>
            
            <div style={{ padding: '15px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', marginBottom: '25px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '8px' }}>
                Discovered: {getDiscoveredCount()}/16 plants
              </div>
              <div style={{ fontSize: '14px', color: '#94a3b8' }}>
                Mastery: {getMasteredCount()}/16 fully mastered
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {Object.entries(plantTypes).map(([key, plant]) => {
                const stats = plantStats[key];
                const mastery = getMasteryLevel(key);
                const isDiscovered = stats.discovered;
                
                return (
                  <div 
                    key={key} 
                    onClick={() => isDiscovered && setSelectedCodexPlant(key)}
                    style={{ 
                      padding: '15px', 
                      background: isDiscovered ? 'rgba(51, 65, 85, 0.5)' : 'rgba(30, 41, 59, 0.8)', 
                      borderRadius: '8px', 
                      border: `2px solid ${mastery.color}`,
                      cursor: isDiscovered ? 'pointer' : 'default',
                      transition: 'transform 0.2s',
                      opacity: isDiscovered ? 1 : 0.5
                    }}
                    onMouseEnter={(e) => isDiscovered && (e.currentTarget.style.transform = 'scale(1.02)')}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '32px' }}>{isDiscovered ? getEmoji(key, 3) : '❓'}</span>
                        <div>
                          <div style={{ fontWeight: 'bold', fontSize: '16px', color: isDiscovered ? '#e2e8f0' : '#64748b' }}>
                            {isDiscovered ? plant.name : '???'}
                          </div>
                          <div style={{ fontSize: '12px', color: mastery.color, fontWeight: 'bold' }}>
                            {isDiscovered ? mastery.level : 'Locked'}
                          </div>
                        </div>
                      </div>
                      {isDiscovered && stats.grown > 0 && (
                        <div style={{ textAlign: 'right', fontSize: '12px', color: '#94a3b8' }}>
                          <div>Grown: {stats.grown}x</div>
                          <div>Earned: ${stats.earned}</div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            <button onClick={() => setShowCodex(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close</button>
          </div>
        </div>
      )}

      {/* Plant Detail View */}
      {showCodex && selectedCodexPlant && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', border: `3px solid ${getMasteryLevel(selectedCodexPlant).color}`, maxWidth: '600px', maxHeight: '85vh', overflowY: 'auto' }}>
            {(() => {
              const plant = plantTypes[selectedCodexPlant];
              const stats = plantStats[selectedCodexPlant];
              const mastery = getMasteryLevel(selectedCodexPlant);
              const lore = PLANT_LORE[selectedCodexPlant];
              
              return (
                <>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <div style={{ fontSize: '64px', marginBottom: '10px' }}>{getEmoji(selectedCodexPlant, 3)}</div>
                    <h2 style={{ margin: '0 0 10px 0', color: '#22c55e', fontSize: '28px' }}>{plant.name}</h2>
                    <div style={{ fontSize: '16px', color: mastery.color, fontWeight: 'bold', padding: '8px 16px', background: `${mastery.color}22`, borderRadius: '6px', display: 'inline-block' }}>
                      {mastery.level === 'MASTERED' ? '⭐ ' : ''}{mastery.level}
                      {mastery.bonus > 0 && ` (+${Math.round(mastery.bonus * 100)}% sell bonus)`}
                    </div>
                  </div>

                  <div style={{ padding: '15px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                    <h3 style={{ fontSize: '16px', color: '#3b82f6', marginTop: 0, marginBottom: '10px' }}>📊 Your Stats</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px', color: '#94a3b8' }}>
                      <div>• Grown: <span style={{ color: '#e2e8f0', fontWeight: 'bold' }}>{stats.grown} times</span></div>
                      <div>• Sold: <span style={{ color: '#e2e8f0', fontWeight: 'bold' }}>{stats.sold} plants</span></div>
                      <div>• Total Earned: <span style={{ color: '#10b981', fontWeight: 'bold' }}>${stats.earned}</span></div>
                      <div>• Fastest Growth: <span style={{ color: '#e2e8f0', fontWeight: 'bold' }}>
                        {stats.fastestGrowth ? `${Math.floor(stats.fastestGrowth / 1000)}s` : 'N/A'}
                      </span></div>
                      <div style={{ gridColumn: '1 / -1' }}>• Success Rate: <span style={{ color: '#e2e8f0', fontWeight: 'bold' }}>
                        {stats.grown > 0 ? Math.round((stats.sold / stats.grown) * 100) : 0}%
                      </span></div>
                    </div>
                  </div>

                  <div style={{ padding: '15px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                    <h3 style={{ fontSize: '16px', color: '#8b5cf6', marginTop: 0, marginBottom: '10px' }}>📖 Plant Info</h3>
                    <p style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6', fontStyle: 'italic', margin: '0 0 15px 0' }}>
                      "{lore.description}"
                    </p>
                    <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '1.8' }}>
                      <div><strong style={{ color: '#e2e8f0' }}>Water:</strong> {plant.waterNeed}% (moderate)</div>
                      <div><strong style={{ color: '#e2e8f0' }}>Light:</strong> {plant.lightNeed}%</div>
                      <div><strong style={{ color: '#e2e8f0' }}>Temperature:</strong> {plant.tempNeed}°F</div>
                      <div><strong style={{ color: '#e2e8f0' }}>Best Season:</strong> {plant.preferredSeason} {SEASONS[plant.preferredSeason].icon}</div>
                      <div><strong style={{ color: '#e2e8f0' }}>Climate:</strong> {plant.climate}</div>
                      <div><strong style={{ color: '#e2e8f0' }}>Pest Resistance:</strong> {Math.round(plant.pestResistance * 100)}%</div>
                      <div><strong style={{ color: '#e2e8f0' }}>Value:</strong> ${plant.sellPrice}</div>
                      <div><strong style={{ color: '#e2e8f0' }}>Growth Time:</strong> ~{Math.round(plant.growthTime / 1000)}s</div>
                    </div>
                  </div>

                  <div style={{ padding: '15px', background: 'rgba(251, 191, 36, 0.1)', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                    <h3 style={{ fontSize: '16px', color: '#fbbf24', marginTop: 0, marginBottom: '10px' }}>🏆 Milestones</h3>
                    <div style={{ fontSize: '13px', color: '#94a3b8', lineHeight: '1.8' }}>
                      <div style={{ opacity: stats.grown >= 1 ? 1 : 0.5 }}>
                        {stats.grown >= 1 ? '✓' : '○'} Grow 1 → Reward: $10 {stats.grown >= 1 && '✓'}
                      </div>
                      <div style={{ opacity: stats.grown >= 10 ? 1 : 0.5 }}>
                        {stats.grown >= 10 ? '✓' : '○'} Grow 10 → Reward: $50 {stats.grown >= 10 && '✓'}
                      </div>
                      <div style={{ opacity: stats.grown >= 50 ? 1 : 0.5 }}>
                        {stats.grown >= 50 ? '✓' : '○'} Grow 50 → MASTERED status {stats.grown >= 50 && '✓'}
                      </div>
                      <div style={{ opacity: 0.5 }}>
                        ○ Grow 100 → Reward: ???
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: '15px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(16, 185, 129, 0.3)' }}>
                    <h3 style={{ fontSize: '16px', color: '#10b981', marginTop: 0, marginBottom: '10px' }}>💡 Pro Tips</h3>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.8' }}>
                      {lore.tips.map((tip, i) => (
                        <li key={i}>{tip}</li>
                      ))}
                    </ul>
                  </div>

                  {stats.grown >= 10 && (
                    <div style={{ padding: '15px', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                      <h3 style={{ fontSize: '16px', color: '#22c55e', marginTop: 0, marginBottom: '10px' }}>🔬 Fun Facts</h3>
                      <div style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.8' }}>
                        {lore.facts.slice(0, Math.min(3, Math.floor(stats.grown / 10))).map((fact, i) => (
                          <div key={i} style={{ marginBottom: '8px' }}>• {fact}</div>
                        ))}
                      </div>
                    </div>
                  )}

                  <button onClick={() => setSelectedCodexPlant(null)} style={{ marginTop: '10px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>← Back to Codex</button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Prestige Modal */}
      {showPrestigeModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.9)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 200 }}>
          <div style={{ background: 'linear-gradient(135deg, #1e293b, #0f172a)', padding: '40px', borderRadius: '16px', maxWidth: '550px', border: '3px solid #8b5cf6', boxShadow: '0 8px 32px rgba(139, 92, 246, 0.5)', width: '90%', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{ fontSize: '64px', marginBottom: '10px' }}>🔄</div>
              <h2 style={{ margin: '0 0 10px 0', color: '#a855f7', fontSize: '32px' }}>PRESTIGE MODE</h2>
              <div style={{ fontSize: '14px', color: '#94a3b8' }}>
                Current Level: {prestigeLevel} | Next: {prestigeLevel + 1}
              </div>
            </div>

            <div style={{ marginBottom: '25px', padding: '20px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '8px', border: '2px solid #ef4444' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#ef4444', marginBottom: '12px' }}>❌ YOU WILL LOSE:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#fca5a5', lineHeight: '1.8' }}>
                <li>All money (${money})</li>
                <li>All plants currently growing</li>
                <li>All upgrades (except global bonuses)</li>
                <li>Greenhouse #2 and #3 unlocks</li>
              </ul>
            </div>

            <div style={{ marginBottom: '25px', padding: '20px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '8px', border: '2px solid #10b981' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#10b981', marginBottom: '12px' }}>✓ YOU WILL KEEP:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#6ee7b7', lineHeight: '1.8' }}>
                <li>All achievements ({unlockedAchievements.length})</li>
                <li>Plant encyclopedia progress ({getDiscoveredCount()}/8 discovered)</li>
                <li>Daily streak ({dailyReward.streakDay} days) - PRESERVED!</li>
                <li>Plant mastery bonuses</li>
              </ul>
            </div>

            <div style={{ marginBottom: '25px', padding: '20px', background: 'rgba(139, 92, 246, 0.2)', borderRadius: '8px', border: '2px solid #8b5cf6' }}>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#a855f7', marginBottom: '12px' }}>🎁 YOU WILL GAIN:</div>
              <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#c4b5fd', lineHeight: '1.8' }}>
                <li><strong>+{(prestigeLevel + 1) * 25}% sell price</strong> (PERMANENT)</li>
                <li><strong>+{(prestigeLevel + 1) * 20}% growth speed</strong> (PERMANENT)</li>
                <li>Prestige badge next to your name</li>
                {prestigeLevel + 1 >= 2 && <li>Access to Prestige Garden (4th greenhouse)</li>}
                {prestigeLevel + 1 >= 3 && <li>Start with $500 instead of $50</li>}
                {prestigeLevel + 1 >= 4 && <li>Start with Greenhouse #2 unlocked</li>}
                {prestigeLevel + 1 >= 5 && <li>ALL plant seeds unlocked from start</li>}
                {prestigeLevel + 1 >= 10 && <li style={{ color: '#fbbf24', fontWeight: 'bold' }}>💎 DIAMOND BADGE</li>}
              </ul>
            </div>

            {!canPrestige() && (
              <div style={{ padding: '15px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: '8px', marginBottom: '20px', border: '2px solid #ef4444' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#ef4444', marginBottom: '8px' }}>Requirements Not Met:</div>
                <div style={{ fontSize: '13px', color: '#fca5a5', lineHeight: '1.6' }}>
                  {stats.totalEarned < 10000 && <div>• Need $10,000 total earned (you have ${stats.totalEarned})</div>}
                  {stats.greenhousesOwned < 3 && <div>• Need all 3 greenhouses (you have {stats.greenhousesOwned})</div>}
                  {unlockedAchievements.length < 5 && <div>• Need 5 achievements (you have {unlockedAchievements.length})</div>}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button 
                onClick={doPrestige} 
                disabled={!canPrestige()}
                style={{ 
                  flex: 1, 
                  padding: '15px', 
                  background: canPrestige() ? 'linear-gradient(135deg, #8b5cf6, #a855f7)' : '#64748b', 
                  border: 'none', 
                  borderRadius: '8px', 
                  color: 'white', 
                  cursor: canPrestige() ? 'pointer' : 'not-allowed', 
                  fontSize: '16px', 
                  fontWeight: 'bold',
                  boxShadow: canPrestige() ? '0 4px 12px rgba(139, 92, 246, 0.4)' : 'none'
                }}
              >
                Start Prestige {prestigeLevel + 1}
              </button>
              <button onClick={() => setShowPrestigeModal(false)} style={{ flex: 1, padding: '15px', background: '#64748b', border: 'none', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Story Quest Chains Modal */}
      {showStoryQuests && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', padding: '30px', borderRadius: '12px', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto', border: '3px solid #a855f7', width: '90%', boxShadow: '0 8px 32px rgba(168, 85, 247, 0.5)' }}>
            <h2 style={{ marginTop: 0, color: '#a855f7', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              📖 Story Quest Chains
            </h2>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '25px' }}>
              Complete multi-part quest chains for legendary rewards and permanent bonuses!
            </p>

            {/* Botanist's Challenge */}
            {storyQuests.botanistChallenge.started && (
              <div style={{ marginBottom: '30px', padding: '20px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px', border: storyQuests.botanistChallenge.completed ? '2px solid #10b981' : '2px solid #3b82f6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: '#60a5fa', fontSize: '18px' }}>
                    🧪 {STORY_QUESTS.botanistChallenge.name}
                  </h3>
                  {storyQuests.botanistChallenge.completed && (
                    <div style={{ padding: '6px 12px', background: '#10b981', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', color: 'white' }}>
                      ✓ COMPLETE
                    </div>
                  )}
                </div>

                {/* Progress Bar */}
                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', marginBottom: '5px' }}>
                    <span>Progress</span>
                    <span>{storyQuests.botanistChallenge.partsCompleted.filter(p => p).length}/5 parts</span>
                  </div>
                  <div style={{ height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${(storyQuests.botanistChallenge.partsCompleted.filter(p => p).length / 5) * 100}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>

                {/* Quest Parts */}
                {STORY_QUESTS.botanistChallenge.parts.map((part, index) => {
                  const isComplete = storyQuests.botanistChallenge.partsCompleted[index];
                  const isClaimed = storyQuests.botanistChallenge.rewardsClaimed[index];
                  const isActive = index === storyQuests.botanistChallenge.currentPart && !isComplete;
                  
                  return (
                    <div key={index} style={{ 
                      padding: '12px', 
                      background: isComplete ? 'rgba(16, 185, 129, 0.1)' : isActive ? 'rgba(59, 130, 246, 0.2)' : 'rgba(51, 65, 85, 0.3)',
                      borderRadius: '8px', 
                      marginBottom: '8px',
                      border: isComplete ? '2px solid #10b981' : isActive ? '2px solid #3b82f6' : '2px solid transparent',
                      opacity: (!isActive && !isComplete) ? 0.5 : 1
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: isComplete ? '#10b981' : '#e2e8f0', marginBottom: '4px' }}>
                            {isComplete ? '✓' : isActive ? '▶' : `${index + 1}.`} {part.goal}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>{part.desc}</div>
                          <div style={{ fontSize: '11px', color: '#fbbf24', marginTop: '4px' }}>
                            Reward: {part.reward.type === 'money' ? `$${part.reward.amount}` : 
                                    part.reward.type === 'rare' ? 'Rare Seed' :
                                    part.reward.type === 'title' ? 'Title' :
                                    part.reward.type === 'permanent' ? `+${Math.round(part.reward.amount * 100)}% Growth` :
                                    'Legendary Reward'}
                          </div>
                        </div>
                        {isComplete && !isClaimed && (
                          <button 
                            onClick={() => claimStoryReward('botanistChallenge', index)}
                            style={{ 
                              padding: '8px 16px', 
                              background: '#10b981', 
                              border: 'none', 
                              borderRadius: '6px', 
                              color: 'white', 
                              cursor: 'pointer', 
                              fontSize: '13px', 
                              fontWeight: 'bold',
                              marginLeft: '10px'
                            }}
                          >
                            Claim!
                          </button>
                        )}
                        {isClaimed && (
                          <div style={{ padding: '8px 16px', background: '#64748b', borderRadius: '6px', fontSize: '12px', color: 'white', marginLeft: '10px' }}>
                            Claimed
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Master Gardener Path */}
            {storyQuests.masterGardener.started && (
              <div style={{ marginBottom: '30px', padding: '20px', background: 'rgba(16, 185, 129, 0.1)', borderRadius: '12px', border: storyQuests.masterGardener.completed ? '2px solid #10b981' : '2px solid #22c55e' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: '#22c55e', fontSize: '18px' }}>
                    🌱 {STORY_QUESTS.masterGardener.name}
                  </h3>
                  {storyQuests.masterGardener.completed && (
                    <div style={{ padding: '6px 12px', background: '#10b981', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', color: 'white' }}>
                      ✓ COMPLETE
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', marginBottom: '5px' }}>
                    <span>Progress</span>
                    <span>{storyQuests.masterGardener.partsCompleted.filter(p => p).length}/5 parts</span>
                  </div>
                  <div style={{ height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${(storyQuests.masterGardener.partsCompleted.filter(p => p).length / 5) * 100}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #22c55e, #4ade80)',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>

                {STORY_QUESTS.masterGardener.parts.map((part, index) => {
                  const isComplete = storyQuests.masterGardener.partsCompleted[index];
                  const isClaimed = storyQuests.masterGardener.rewardsClaimed[index];
                  const isActive = index === storyQuests.masterGardener.currentPart && !isComplete;
                  
                  return (
                    <div key={index} style={{ 
                      padding: '12px', 
                      background: isComplete ? 'rgba(16, 185, 129, 0.15)' : isActive ? 'rgba(34, 197, 94, 0.2)' : 'rgba(51, 65, 85, 0.3)',
                      borderRadius: '8px', 
                      marginBottom: '8px',
                      border: isComplete ? '2px solid #10b981' : isActive ? '2px solid #22c55e' : '2px solid transparent',
                      opacity: (!isActive && !isComplete) ? 0.5 : 1
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: isComplete ? '#10b981' : '#e2e8f0', marginBottom: '4px' }}>
                            {isComplete ? '✓' : isActive ? '▶' : `${index + 1}.`} {part.goal}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>{part.desc}</div>
                          <div style={{ fontSize: '11px', color: '#fbbf24', marginTop: '4px' }}>
                            Reward: {part.reward.type === 'money' ? `$${part.reward.amount}` : 
                                    part.reward.type === 'rare' ? 'Rare Seed' :
                                    part.reward.type === 'legendary' ? '👑 Golden Lotus!' :
                                    'Legendary Reward'}
                          </div>
                        </div>
                        {isComplete && !isClaimed && (
                          <button 
                            onClick={() => claimStoryReward('masterGardener', index)}
                            style={{ 
                              padding: '8px 16px', 
                              background: '#10b981', 
                              border: 'none', 
                              borderRadius: '6px', 
                              color: 'white', 
                              cursor: 'pointer', 
                              fontSize: '13px', 
                              fontWeight: 'bold',
                              marginLeft: '10px'
                            }}
                          >
                            Claim!
                          </button>
                        )}
                        {isClaimed && (
                          <div style={{ padding: '8px 16px', background: '#64748b', borderRadius: '6px', fontSize: '12px', color: 'white', marginLeft: '10px' }}>
                            Claimed
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Rare Plant Hunter */}
            {storyQuests.rareHunter.started && (
              <div style={{ marginBottom: '20px', padding: '20px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '12px', border: storyQuests.rareHunter.completed ? '2px solid #10b981' : '2px solid #a855f7' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ margin: 0, color: '#a855f7', fontSize: '18px' }}>
                    ✨ {STORY_QUESTS.rareHunter.name}
                  </h3>
                  {storyQuests.rareHunter.completed && (
                    <div style={{ padding: '6px 12px', background: '#10b981', borderRadius: '6px', fontSize: '12px', fontWeight: 'bold', color: 'white' }}>
                      ✓ COMPLETE
                    </div>
                  )}
                </div>

                <div style={{ marginBottom: '15px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', marginBottom: '5px' }}>
                    <span>Progress</span>
                    <span>{storyQuests.rareHunter.partsCompleted.filter(p => p).length}/4 parts</span>
                  </div>
                  <div style={{ height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${(storyQuests.rareHunter.partsCompleted.filter(p => p).length / 4) * 100}%`, 
                      height: '100%', 
                      background: 'linear-gradient(90deg, #a855f7, #c084fc)',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>

                {STORY_QUESTS.rareHunter.parts.map((part, index) => {
                  const isComplete = storyQuests.rareHunter.partsCompleted[index];
                  const isClaimed = storyQuests.rareHunter.rewardsClaimed[index];
                  const isActive = index === storyQuests.rareHunter.currentPart && !isComplete;
                  
                  return (
                    <div key={index} style={{ 
                      padding: '12px', 
                      background: isComplete ? 'rgba(16, 185, 129, 0.1)' : isActive ? 'rgba(168, 85, 247, 0.2)' : 'rgba(51, 65, 85, 0.3)',
                      borderRadius: '8px', 
                      marginBottom: '8px',
                      border: isComplete ? '2px solid #10b981' : isActive ? '2px solid #a855f7' : '2px solid transparent',
                      opacity: (!isActive && !isComplete) ? 0.5 : 1
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 'bold', color: isComplete ? '#10b981' : '#e2e8f0', marginBottom: '4px' }}>
                            {isComplete ? '✓' : isActive ? '▶' : `${index + 1}.`} {part.goal}
                          </div>
                          <div style={{ fontSize: '11px', color: '#94a3b8' }}>{part.desc}</div>
                          <div style={{ fontSize: '11px', color: '#fbbf24', marginTop: '4px' }}>
                            Reward: {part.reward.type === 'money' ? `$${part.reward.amount}` : 
                                    part.reward.type === 'rare' ? 'Rare Seed' :
                                    part.reward.type === 'legendary' ? '👑 Golden Lotus!' :
                                    'Legendary Reward'}
                          </div>
                        </div>
                        {isComplete && !isClaimed && (
                          <button 
                            onClick={() => claimStoryReward('rareHunter', index)}
                            style={{ 
                              padding: '8px 16px', 
                              background: '#10b981', 
                              border: 'none', 
                              borderRadius: '6px', 
                              color: 'white', 
                              cursor: 'pointer', 
                              fontSize: '13px', 
                              fontWeight: 'bold',
                              marginLeft: '10px'
                            }}
                          >
                            Claim!
                          </button>
                        )}
                        {isClaimed && (
                          <div style={{ padding: '8px 16px', background: '#64748b', borderRadius: '6px', fontSize: '12px', color: 'white', marginLeft: '10px' }}>
                            Claimed
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {!storyQuests.botanistChallenge.started && (
              <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>📖</div>
                <p>Story quests will unlock as you progress through the game!</p>
              </div>
            )}

            <button onClick={() => setShowStoryQuests(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close</button>
          </div>
        </div>
      )}

      {/* Weekly Challenges Modal */}
      {showChallenges && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }}>
          <div style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', padding: '30px', borderRadius: '12px', maxWidth: '650px', maxHeight: '85vh', overflowY: 'auto', border: '3px solid #f59e0b', width: '90%', boxShadow: '0 8px 32px rgba(245, 158, 11, 0.5)' }}>
            <h2 style={{ marginTop: 0, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              🏆 Weekly Challenges
            </h2>
            <p style={{ fontSize: '13px', color: '#94a3b8', marginBottom: '25px' }}>
              Complete time-limited challenges for exclusive rewards! New challenges every week.
            </p>

            {activeChallenges.length === 0 && (
              <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>🔒</div>
                <p>Challenges unlock after your first Prestige!</p>
              </div>
            )}

            {activeChallenges.map((challenge, index) => {
              const progress = challengeProgress[challenge.id] || 0;
              const percentage = Math.min(100, (progress / challenge.goal) * 100);
              const timeElapsed = challenge.startTime ? Math.floor((Date.now() - challenge.startTime) / 1000) : 0;
              const timeRemaining = challenge.timeLimit ? challenge.timeLimit - timeElapsed : null;

              return (
                <div key={challenge.id} style={{ 
                  marginBottom: '20px', 
                  padding: '20px', 
                  background: challenge.completed ? 'rgba(16, 185, 129, 0.1)' : challenge.active ? 'rgba(245, 158, 11, 0.2)' : 'rgba(51, 65, 85, 0.5)',
                  borderRadius: '12px', 
                  border: challenge.completed ? '2px solid #10b981' : challenge.active ? '2px solid #f59e0b' : '2px solid #475569'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '5px' }}>
                        {challenge.icon} {challenge.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#94a3b8' }}>{challenge.desc}</div>
                      <div style={{ fontSize: '12px', color: '#fbbf24', marginTop: '5px' }}>
                        Reward: {challenge.reward.display}
                      </div>
                    </div>
                    {!challenge.active && !challenge.completed && (
                      <button 
                        onClick={() => startChallenge(challenge.id)}
                        style={{ 
                          padding: '10px 20px', 
                          background: '#f59e0b', 
                          border: 'none', 
                          borderRadius: '6px', 
                          color: 'white', 
                          cursor: 'pointer', 
                          fontSize: '14px', 
                          fontWeight: 'bold'
                        }}
                      >
                        Start
                      </button>
                    )}
                    {challenge.completed && !challenge.claimed && (
                      <button 
                        onClick={() => claimChallengeReward(challenge.id)}
                        style={{ 
                          padding: '10px 20px', 
                          background: '#10b981', 
                          border: 'none', 
                          borderRadius: '6px', 
                          color: 'white', 
                          cursor: 'pointer', 
                          fontSize: '14px', 
                          fontWeight: 'bold'
                        }}
                      >
                        Claim!
                      </button>
                    )}
                    {challenge.claimed && (
                      <div style={{ padding: '10px 20px', background: '#64748b', borderRadius: '6px', fontSize: '13px', color: 'white' }}>
                        Claimed
                      </div>
                    )}
                  </div>

                  {challenge.active && (
                    <>
                      <div style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#94a3b8', marginBottom: '5px' }}>
                          <span>Progress</span>
                          <span>{Math.floor(progress)}/{challenge.goal}</span>
                        </div>
                        <div style={{ height: '8px', background: '#1e293b', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ 
                            width: `${percentage}%`, 
                            height: '100%', 
                            background: 'linear-gradient(90deg, #f59e0b, #fbbf24)',
                            transition: 'width 0.3s'
                          }} />
                        </div>
                      </div>
                      {timeRemaining !== null && (
                        <div style={{ fontSize: '13px', color: timeRemaining < 30 ? '#ef4444' : '#fbbf24', fontWeight: 'bold' }}>
                          ⏱️ Time Remaining: {Math.floor(timeRemaining / 60)}:{String(timeRemaining % 60).padStart(2, '0')}
                        </div>
                      )}
                    </>
                  )}

                  {challenge.maxPots && challenge.active && (
                    <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '10px' }}>
                      ⚠️ You can only use {challenge.maxPots} pots for this challenge
                    </div>
                  )}
                </div>
              );
            })}

            <button onClick={() => setShowChallenges(false)} style={{ marginTop: '20px', padding: '10px 20px', background: '#64748b', border: 'none', borderRadius: '6px', color: 'white', cursor: 'pointer', width: '100%' }}>Close</button>
          </div>
        </div>
      )}

      {/* Gardener's Almanac Modal */}
      {showAlmanac && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0, 0, 0, 0.85)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 100 }} onClick={() => setShowAlmanac(false)}>
          <div style={{ background: 'linear-gradient(180deg, #059669, #047857)', padding: '20px', borderRadius: '16px', width: '250px', border: '3px solid #10b981', boxShadow: '0 8px 32px rgba(16, 185, 129, 0.5)', display: 'flex', flexDirection: 'column', gap: '8px' }} onClick={(e) => e.stopPropagation()}>
            
            <h2 style={{ margin: '0 0 20px 0', color: 'white', fontSize: '20px', textAlign: 'center', borderBottom: '2px solid rgba(255,255,255,0.3)', paddingBottom: '15px' }}>
              📗 Gardener's<br/>Almanac
            </h2>
            
            <button 
              onClick={() => { setShowAlmanac(false); setShowStoryQuests(true); }}
              style={{ 
                padding: '14px', 
                background: 'rgba(255,255,255,0.15)', 
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '8px', 
                color: 'white', 
                cursor: 'pointer', 
                fontSize: '15px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            >
              📖 Story Quests
            </button>
            
            <button 
              onClick={() => { setShowAlmanac(false); setShowQuestsModal(true); }}
              style={{ 
                padding: '14px', 
                background: 'rgba(255,255,255,0.15)', 
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '8px', 
                color: 'white', 
                cursor: 'pointer', 
                fontSize: '15px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            >
              📋 Daily Quests
            </button>
            
            <button 
              onClick={() => { setShowAlmanac(false); setShowCodex(true); }}
              style={{ 
                padding: '14px', 
                background: 'rgba(255,255,255,0.15)', 
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '8px', 
                color: 'white', 
                cursor: 'pointer', 
                fontSize: '15px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            >
              🌿 Plant Codex
            </button>
            
            {prestigeLevel >= 1 && (
              <button 
                onClick={() => { setShowAlmanac(false); setShowChallenges(true); }}
                style={{ 
                  padding: '14px', 
                  background: 'rgba(255,255,255,0.15)', 
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderRadius: '8px', 
                  color: 'white', 
                  cursor: 'pointer', 
                  fontSize: '15px',
                  textAlign: 'left',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
                onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
              >
                🏆 Challenges
              </button>
            )}
            
            <button 
              onClick={() => { setShowAlmanac(false); setShowAchievements(true); }}
              style={{ 
                padding: '14px', 
                background: 'rgba(255,255,255,0.15)', 
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '8px', 
                color: 'white', 
                cursor: 'pointer', 
                fontSize: '15px',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontWeight: 'bold',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.25)'}
              onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.15)'}
            >
              🏅 Achievements
            </button>
            
            <button 
              onClick={() => setShowAlmanac(false)}
              style={{ 
                marginTop: '10px',
                padding: '12px', 
                background: '#dc2626', 
                border: '2px solid white',
                borderRadius: '8px', 
                color: 'white', 
                cursor: 'pointer', 
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              ✕ Close
            </button>
          </div>
        </div>
      )}

      {/* Tutorial Overlay */}
      {showTutorial && !tutorialCompleted && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'transparent', display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', zIndex: 300, pointerEvents: 'none', paddingLeft: '20px', paddingBottom: '20px' }}>
          <div style={{ background: 'linear-gradient(135deg, #1e293b, #334155)', padding: '30px', borderRadius: '12px', border: '3px solid #4ade80', boxShadow: '0 8px 32px rgba(74, 222, 128, 0.5)', maxWidth: '400px', width: '90%', pointerEvents: 'auto' }}>
            <h2 style={{ marginTop: 0, marginBottom: '15px', color: '#4ade80' }}>
              🌱 Welcome to Sprout & Bloom!
            </h2>
            
            {tutorialStep === 0 && (
              <div>
                <p style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.6' }}>
                  Welcome! Let's get you started. First, <strong>plant your first seed</strong> by clicking the "🌱 Plant Seed" button in an empty pot.
                </p>
                <div style={{ fontSize: '48px', textAlign: 'center', margin: '20px 0' }}>👇</div>
              </div>
            )}

            {tutorialStep === 1 && (
              <div>
                <p style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.6' }}>
                  Great! Now <strong>water your plant</strong> to help it grow by clicking the "💧 Water" button.
                </p>
                <div style={{ fontSize: '48px', textAlign: 'center', margin: '20px 0' }}>💧</div>
              </div>
            )}

            {tutorialStep === 2 && (
              <div>
                <p style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.6' }}>
                  Perfect! Now <strong>watch the growth bar fill up</strong>. Your plant will grow through 4 stages. Keep watering to maintain health!
                </p>
                <div style={{ fontSize: '48px', textAlign: 'center', margin: '20px 0' }}>📈</div>
              </div>
            )}

            {tutorialStep === 3 && (
              <div>
                <p style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.6' }}>
                  Looking good! Your plant is growing. Once it reaches <strong>Stage 4/4</strong>, you can harvest it for money!
                </p>
                <div style={{ fontSize: '48px', textAlign: 'center', margin: '20px 0' }}>🌺</div>
              </div>
            )}

            {tutorialStep === 4 && (
              <div>
                <p style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.6' }}>
                  Your plant is ready! <strong>Harvest it</strong> by clicking the "💰 Sell" button to earn money!
                </p>
                <div style={{ fontSize: '48px', textAlign: 'center', margin: '20px 0' }}>💰</div>
              </div>
            )}

            {tutorialStep === 5 && (
              <div>
                <p style={{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.6', marginBottom: '15px' }}>
                  Excellent work! 🎉 You've learned the basics. Now explore:
                </p>
                <ul style={{ fontSize: '13px', color: '#cbd5e1', lineHeight: '1.8', paddingLeft: '20px' }}>
                  <li>Check the <strong>Shop</strong> for upgrades and new plants</li>
                  <li>Complete <strong>Quests</strong> for rewards</li>
                  <li>Unlock the <strong>Codex</strong> to track your progress</li>
                  <li>Build up to <strong>Prestige</strong> for permanent bonuses!</li>
                </ul>
                <button 
                  onClick={() => {
                    setShowTutorial(false);
                    setTutorialCompleted(true);
                    setMoney(m => m + 50);
                    setFlyInventory(f => f + 5);
                    showNotification('🎓 Tutorial Complete! +$50 +5 Flies');
                  }}
                  style={{ 
                    width: '100%', 
                    marginTop: '20px',
                    padding: '12px', 
                    background: '#10b981', 
                    border: 'none', 
                    borderRadius: '6px', 
                    color: 'white', 
                    cursor: 'pointer', 
                    fontSize: '16px', 
                    fontWeight: 'bold'
                  }}
                >
                  Finish Tutorial (Get $50 + 5 Flies!)
                </button>
              </div>
            )}

            {tutorialStep < 5 && (
              <button 
                onClick={() => {
                  setShowTutorial(false);
                  setTutorialCompleted(true);
                }}
                style={{ 
                  width: '100%', 
                  marginTop: '15px',
                  padding: '8px', 
                  background: '#64748b', 
                  border: 'none', 
                  borderRadius: '6px', 
                  color: 'white', 
                  cursor: 'pointer', 
                  fontSize: '13px'
                }}
              >
                Skip Tutorial
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}